// Score engraving (§4.7–4.10): measures bars, breaks lines, justifies systems
// and produces a display list per system plus the layout map used for hit
// testing, the cursor, selection bands and playhead interpolation.

import {
  type Pitch,
  type TimeSig,
  type AccidentalMark,
  ticks as durTicks,
  barCapacity,
  computeAccidentals,
  diatonicIndex,
  midiOf,
  noteLabel,
  spellMidi,
  normalizeFifths,
  mod,
  WHOLE,
} from '@simplenotation/theory';
import {
  type Score,
  type Beat,
  type Note,
  type Clef,
  type BarInfo,
  timeSigAt,
  keySigAt,
  clefAt,
  middleLineOf,
  displayBarNumbers,
  trackKeyAt,
} from '@simplenotation/core';
import { G, ENGRAVING } from './glyphs';
import { beamGroups, beamCount, autoStemDir, tupletGroups, beamable } from './beams';
import type { DrawItem, Layout, LayoutOptions, Band, BarBox, BeatBox, SystemBox, Column } from './types';

// ---------------------------------------------------------------- constants

const STAFF_LINES = 5;
const TAB_GAP = 1.5; // in staff spaces
const BOOMWHACKERS = ['#e21c48', '#f26622', '#f99d1c', '#ffcc33', '#fff32b', '#bcd85f', '#62bc47', '#009c95', '#0071bb', '#5e50a1', '#8d5ba6', '#cf3e96'];

const SHARP_POS_TREBLE = [4, 1, 5, 2, -1, 3, 0];
const FLAT_POS_TREBLE = [0, 3, -1, 2, -2, 1, -3];
const SHARP_POS_TENOR = [-2, 2, -1, 3, 0, 4, 1];

function keyPositions(fifths: number, clef: Clef): { pos: number[]; glyph: string } {
  const n = Math.abs(fifths);
  if (n === 0) return { pos: [], glyph: '' };
  const offset = clef === 'bass' ? -2 : clef === 'alto' ? -1 : clef === 'tenor' ? 1 : 0;
  if (fifths > 0) {
    const base = clef === 'tenor' ? SHARP_POS_TENOR : SHARP_POS_TREBLE.map((p) => p + offset);
    return { pos: base.slice(0, n), glyph: G.accidentalSharp.ch };
  }
  return { pos: FLAT_POS_TREBLE.slice(0, n).map((p) => p + offset), glyph: G.accidentalFlat.ch };
}

const ACC_GLYPH: Record<number, keyof typeof G> = {
  [-2]: 'accidentalDoubleFlat',
  [-1]: 'accidentalFlat',
  0: 'accidentalNatural',
  1: 'accidentalSharp',
  2: 'accidentalDoubleSharp',
};

const REST_GLYPH: Record<number, keyof typeof G> = {
  1: 'restWhole',
  2: 'restHalf',
  4: 'restQuarter',
  8: 'rest8th',
  16: 'rest16th',
  32: 'rest32nd',
  64: 'rest64th',
};

/**
 * Rests that would complete a bar holding `from` of its `capacity` ticks,
 * written the conventional way: each rest starts on a multiple of its own
 * length (so 4/4 after one crotchet is crotchet + minim, not a dotted minim),
 * and in compound time none crosses a dotted-crotchet beat. Shown faintly in
 * the editor; they are not in the score.
 */
export function impliedRests(from: number, capacity: number, ts: TimeSig): { tick: number; base: number; dots: number }[] {
  const out: { tick: number; base: number; dots: number }[] = [];
  const compound = ts.den === 8 && ts.num % 3 === 0 && ts.num > 3;
  const group = (3 * WHOLE) / 8;
  let pos = from;
  for (let guard = 0; pos < capacity && guard < 64; guard++) {
    const left = capacity - pos;
    if (compound && pos % group === 0 && left >= group) {
      out.push({ tick: pos, base: 4, dots: 1 });
      pos += group;
      continue;
    }
    const limit = compound ? Math.min(left, group - (pos % group)) : left;
    let pick = 0;
    for (const base of [2, 4, 8, 16, 32, 64]) {
      const len = WHOLE / base;
      if (len <= limit && pos % len === 0) {
        pick = base;
        break;
      }
    }
    // Off the grid (after a tuplet, say): take the largest value that fits.
    if (!pick) pick = [2, 4, 8, 16, 32, 64].find((b) => WHOLE / b <= limit) ?? 0;
    if (!pick) break;
    out.push({ tick: pos, base: pick, dots: 0 });
    pos += WHOLE / pick;
  }
  return out;
}

/** Ink of the faint rests that complete a short bar in the editor. */
const IMPLIED_REST_INK = '#c3c6cd';

const DYN_GLYPH: Record<string, keyof typeof G> = {
  ppp: 'dynamicPPP',
  pp: 'dynamicPP',
  p: 'dynamicPiano',
  mp: 'dynamicMP',
  mf: 'dynamicMF',
  f: 'dynamicForte',
  ff: 'dynamicFF',
  fff: 'dynamicFFF',
  fp: 'dynamicFortePiano',
  fz: 'dynamicForzando',
  sf: 'dynamicSforzando1',
  sfz: 'dynamicSforzato',
  sffz: 'dynamicSforzatoFF',
};

function defaultMeasure(text: string, font: string): number {
  const m = /(\d+(?:\.\d+)?)px/.exec(font);
  const size = m ? Number(m[1]) : 12;
  const bold = /\b(bold|[6-9]00)\b/.test(font) ? 1.07 : 1;
  return text.length * size * 0.56 * bold;
}

// ---------------------------------------------------------------- types

interface VoiceData {
  track: number;
  voice: number;
  beats: Beat[];
  onsets: number[];
  /** Faint rests completing a short bar (editor only, see `impliedRests`). */
  implied?: { tick: number; base: number; dots: number }[];
}

interface ColMeasure {
  tick: number;
  gap: number;
  pre: number;
  post: number;
}

interface BarData {
  bar: number;
  info: BarInfo;
  ts: TimeSig;
  capacity: number;
  length: number;
  cols: ColMeasure[];
  voices: VoiceData[];
  acc: Map<string, AccidentalMark>;
  overfull: boolean;
  timeChange: boolean;
  keyChange: boolean;
  clefChange: Map<number, Clef>;
  multiRest: number;
}

interface NoteGeom {
  note: Note;
  pitch: Pitch;
  pos: number;
  x: number;
  y: number;
  displaced: boolean;
}

interface BeatGeom {
  beat: Beat;
  vd: VoiceData;
  idx: number;
  x: number;
  headW: number;
  notes: NoteGeom[];
  stemDir: 1 | -1;
  stemX: number;
  stemTop: number;
  stemBottom: number;
  beamed: boolean;
}

// ---------------------------------------------------------------- caches
// Documents are immutable (immer), so unchanged bars keep their object
// identity across edits. Systems whose inputs are identical reuse their
// display lists; only the systems around an edit are re-engraved.

const oids = new WeakMap<object, number>();
let oidN = 0;
function oid(o: object | undefined | null): number {
  if (!o) return 0;
  let n = oids.get(o);
  if (!n) {
    n = ++oidN;
    oids.set(o, n);
  }
  return n;
}

interface PendingSnapshot {
  ties: [string, { x: number; y: number; up: boolean; id: string }][];
  slurs: [string, { x: number; y: number; above: boolean; id: string }][];
  legato: [string, { x: number; y: number; midi: number; kind: 'h' | 'slide'; id: string }][];
}

interface CachedSystem {
  sys: SystemBox;
  barBoxes: BarBox[];
  beatBoxes: BeatBox[];
  pendingOut: PendingSnapshot;
}

const barCache = new Map<string, BarData>();
const sysCache = new Map<string, CachedSystem>();

/** Drop cached measurements (e.g. after web fonts load). */
export function clearLayoutCache(): void {
  barCache.clear();
  sysCache.clear();
}

// ---------------------------------------------------------------- engraver

export function layoutScore(score: Score, opts: LayoutOptions): Layout {
  return new Engraver(score, opts).run();
}

class Engraver {
  readonly sp: number;
  readonly ink: string;
  readonly tabInk: string;
  readonly measure: (text: string, font: string) => number;
  readonly tracks: number[];
  readonly fontSize: number;
  readonly barNumbers: number[];
  bars: BarData[] = [];
  layoutBars: BarBox[] = [];
  beats: BeatBox[] = [];
  shortestGlobal = WHOLE / 8;
  /** id → items of the system being built (system-local coordinates). */
  curIds = new Map<string, DrawItem[]>();
  optsKey = '';
  trackSig = '';
  /** Pending ties/slurs/legato between beats, keyed per track/voice. */
  pendingTies = new Map<string, { x: number; y: number; up: boolean; system: number; id: string; tab?: { x: number; y: number } }>();
  pendingSlurs = new Map<string, { x: number; y: number; above: boolean; system: number; id: string }>();
  pendingLegato = new Map<string, { x: number; y: number; midi: number; kind: 'h' | 'slide'; system: number; id: string }>();
  pendingHairpins = new Map<string, { x: number; type: 'cresc' | 'dim'; endBeatId: string; system: number }>();

  constructor(
    readonly score: Score,
    readonly opts: LayoutOptions,
  ) {
    this.sp = opts.sp;
    this.ink = opts.ink ?? '#111111';
    this.tabInk = opts.tabInk ?? '#111111';
    this.measure = opts.measure ?? defaultMeasure;
    this.tracks = (opts.tracks ?? score.tracks.map((_, i) => i)).filter((t) => score.tracks[t] && (score.tracks[t].showStaff || score.tracks[t].showTab));
    if (!this.tracks.length) this.tracks = [0];
    this.fontSize = 4 * this.sp;
    this.barNumbers = displayBarNumbers(score);
    const o = opts;
    this.optsKey = [o.sp, o.mode, o.width, o.pageWidth, o.noteNames, o.noteColors, o.viewTranspose, o.impliedRests ? 'ir' : '', this.ink, this.tabInk, this.tracks.join(','), oid(o.measure as object)].join('|');
    this.trackSig = JSON.stringify(this.tracks.map((t) => ({ ...score.tracks[t], bars: undefined })));
    if (barCache.size > 6000) barCache.clear();
    if (sysCache.size > 1500) sysCache.clear();
  }

  barKey(bar: number): string {
    const tb = (b: number) => this.tracks.map((t) => oid(this.score.tracks[t].bars[b])).join(',');
    const ts = timeSigAt(this.score, bar);
    const keys = this.tracks.map((t) => this.viewKey(t, bar) + (bar > 0 ? '/' + this.viewKey(t, bar - 1) : '')).join(',');
    const clefs = this.tracks.map((t) => clefAt(this.score.tracks[t], bar) + (bar > 0 ? '/' + clefAt(this.score.tracks[t], bar - 1) : '')).join(',');
    const prevTs = bar > 0 ? timeSigAt(this.score, bar - 1) : null;
    return `${this.optsKey}|${this.trackSig}|${bar}|${oid(this.score.bars[bar])}|${tb(bar)}|${bar > 0 ? tb(bar - 1) : ''}|${ts.num}/${ts.den}${ts.symbol ?? ''}|${prevTs ? `${prevTs.num}/${prevTs.den}` : ''}|${keys}|${clefs}`;
  }

  // ------------------------------------------------------------ fonts
  get fretFont() {
    return `${(1.3 * this.sp).toFixed(1)}px Arial, Helvetica, sans-serif`;
  }
  get textFont() {
    return `italic ${(1.45 * this.sp).toFixed(1)}px Georgia, 'Times New Roman', serif`;
  }
  get chordFont() {
    return `600 ${(1.55 * this.sp).toFixed(1)}px Inter, 'Helvetica Neue', Arial, sans-serif`;
  }
  get smallFont() {
    return `${(1.15 * this.sp).toFixed(1)}px Inter, 'Helvetica Neue', Arial, sans-serif`;
  }
  get lyricFont() {
    return `${(1.4 * this.sp).toFixed(1)}px Georgia, 'Times New Roman', serif`;
  }

  // ------------------------------------------------------------ pitch access (view transposition)
  pitchOf(note: Note, track: number, bar: number): Pitch {
    const vt = this.opts.viewTranspose ?? 0;
    if (!vt) return note.pitch;
    const key = this.viewKey(track, bar);
    return spellMidi(midiOf(note.pitch) + vt, key);
  }
  viewKey(track: number, bar: number): number {
    const base = trackKeyAt(this.score, track, bar).fifths;
    const vt = this.opts.viewTranspose ?? 0;
    if (!vt) return base;
    return normalizeFifths(base + mod(vt * 7, 12) - (mod(vt * 7, 12) > 6 ? 12 : 0)).fifths;
  }

  // ------------------------------------------------------------ run
  run(): Layout {
    const { sp } = this;
    this.measureBars();
    const marginL = 3 * sp;
    const marginR = 3 * sp;
    const mode = this.opts.mode;
    const pageW = this.opts.pageWidth ?? 816;
    const width = mode === 'paged' ? pageW : this.opts.width;
    const avail = Math.max(20 * sp, width - marginL - marginR);

    // Line breaking (greedy) with the global shortest note for spacing.
    const systemsBars: number[][] = [];
    if (mode === 'horizontal') {
      systemsBars.push(this.bars.map((b) => b.bar));
    } else {
      let cur: number[] = [];
      let curW = 0;
      for (const bd of this.bars) {
        const first = cur.length === 0;
        const headerW = first ? this.headerWidth(bd.bar, systemsBars.length === 0) : 0;
        const w = this.barWidth(bd, this.shortestGlobal, first);
        const prev = cur.length ? this.bars[cur[cur.length - 1]] : null;
        if (!first && curW + w > avail && prev?.info.lineBreak !== 'prevent') {
          systemsBars.push(cur);
          cur = [];
          curW = this.headerWidth(bd.bar, false) + this.barWidth(bd, this.shortestGlobal, true);
          cur.push(bd.bar);
        } else {
          curW += headerW + w;
          cur.push(bd.bar);
        }
        if (bd.info.lineBreak === 'force') {
          systemsBars.push(cur);
          cur = [];
          curW = 0;
        }
      }
      if (cur.length) systemsBars.push(cur);
    }

    // Header (title) items.
    const header: DrawItem[] = [];
    let y = 2 * sp;
    if (this.opts.showTitle !== false) {
      const meta = this.score.meta;
      const titleFont = `${(2.6 * sp).toFixed(1)}px Georgia, 'Times New Roman', serif`;
      header.push({ k: 'text', x: width / 2, y: y + 2.6 * sp, text: meta.title || 'Untitled', font: titleFont, align: 'center', baseline: 'alphabetic', color: this.ink });
      y += 3.6 * sp;
      if (meta.subtitle) {
        header.push({ k: 'text', x: width / 2, y: y + 1.4 * sp, text: meta.subtitle, font: `${(1.5 * sp).toFixed(1)}px Georgia, serif`, align: 'center', color: this.ink });
        y += 2 * sp;
      }
      const credit = [meta.composer, meta.artist && meta.artist !== meta.composer ? meta.artist : undefined, meta.arranger ? `arr. ${meta.arranger}` : undefined].filter(Boolean).join(' · ');
      if (credit) {
        header.push({ k: 'text', x: width - marginR, y: y + 1.2 * sp, text: credit, font: `${(1.3 * sp).toFixed(1)}px Georgia, serif`, align: 'right', color: this.ink });
        y += 1.6 * sp;
      }
      y += 1 * sp;
    }

    // Build systems.
    const systems: SystemBox[] = [];
    const pageH = this.opts.pageHeight ?? 1056;
    const pageGap = 24;
    const pageMarginTop = 4 * sp;
    const pageMarginBottom = 5 * sp;
    const pages: { y0: number; y1: number }[] = [];
    let page = 0;
    if (mode === 'paged') pages.push({ y0: 0, y1: pageH });
    systemsBars.forEach((barIdxs, si) => {
      const sys = this.buildSystem(si, barIdxs, marginL, mode === 'horizontal' ? Infinity : avail, si === systemsBars.length - 1);
      sys.y0 = 0;
      sys.dy = 0;
      const height = sys.y1 - sys.y0;
      if (mode === 'paged') {
        const pageBottom = page * (pageH + pageGap) + pageH - pageMarginBottom;
        if (y + height > pageBottom && si > 0) {
          page++;
          pages.push({ y0: page * (pageH + pageGap), y1: page * (pageH + pageGap) + pageH });
          y = page * (pageH + pageGap) + pageMarginTop;
        }
      }
      this.translateSystem(sys, y - sys.y0);
      sys.page = page;
      y = sys.y1 + 1.5 * sp;
      systems.push(sys);
    });

    let totalWidth = width;
    if (mode === 'horizontal' && systems[0]) totalWidth = Math.max(width, systems[0].x1 + marginR);
    const height = mode === 'paged' ? pages[pages.length - 1].y1 : y + 4 * sp;

    // Index beats and drawable ids.
    const beatIndex = new Map<string, BeatBox>();
    for (const b of this.beats) beatIndex.set(b.beatId, b);
    const itemsById = new Map<string, { sys: SystemBox; items: DrawItem[] }[]>();
    for (const sys of systems) {
      for (const [id, items] of sys.ids) {
        const list = itemsById.get(id);
        if (list) list.push({ sys, items });
        else itemsById.set(id, [{ sys, items }]);
      }
    }

    return {
      width: totalWidth,
      height,
      sp,
      mode,
      header,
      systems,
      bars: this.layoutBars,
      beats: this.beats,
      beatIndex,
      itemsById,
      pages,
    };
  }

  /** Position a system: items stay system-local (painted with sys.dy); geometry becomes absolute. */
  translateSystem(sys: SystemBox, dy: number): void {
    sys.dy = dy;
    sys.y0 += dy;
    sys.y1 += dy;
    sys.top += dy;
    sys.bottom += dy;
    for (const b of sys.bands) {
      b.top += dy;
      b.bottom += dy;
    }
    for (const bb of this.systemBeats.get(sys.index) ?? []) {
      for (let i = 0; i < bb.noteYs.length; i++) bb.noteYs[i] += dy;
      for (let i = 0; i < bb.tabYs.length; i++) bb.tabYs[i] += dy;
    }
  }

  // ------------------------------------------------------------ measurement
  measureBars(): void {
    const { score, sp } = this;
    let shortest = Infinity;
    for (let bar = 0; bar < score.bars.length; bar++) {
      const key = this.barKey(bar);
      const cached = barCache.get(key);
      if (cached) {
        this.bars.push(cached);
        for (const c of cached.cols) shortest = Math.min(shortest, c.gap);
        continue;
      }
      const info = score.bars[bar];
      const ts = timeSigAt(score, bar);
      const capacity = barCapacity(ts);
      const voices: VoiceData[] = [];
      let content = 0;
      for (const t of this.tracks) {
        const tb = score.tracks[t].bars[bar];
        if (!tb) continue;
        tb.voices.forEach((beats, v) => {
          if (!beats || (beats.length === 0 && v > 0)) return;
          const onsets: number[] = [];
          let tick = 0;
          for (const b of beats) {
            onsets.push(tick);
            if (!b.grace) tick += durTicks(b.duration);
          }
          content = Math.max(content, tick);
          const vd: VoiceData = { track: t, voice: v, beats, onsets };
          if (this.opts.impliedRests && v === 0 && beats.length && tick < capacity && !info.pickup && score.tracks[t].showStaff) vd.implied = impliedRests(tick, capacity, ts);
          voices.push(vd);
        });
      }
      const length = info.pickup ? Math.max(content, 1) : Math.max(capacity, content);
      const overfull = content > capacity && !info.pickup;
      // Columns: unique onsets.
      const tickSet = new Set<number>();
      for (const vd of voices) vd.onsets.forEach((o, i) => !vd.beats[i].grace && tickSet.add(o));
      for (const vd of voices) for (const r of vd.implied ?? []) tickSet.add(r.tick);
      if (tickSet.size === 0) tickSet.add(0);
      const ticksSorted = [...tickSet].sort((a, b) => a - b);
      // Accidentals per track staff (all voices merged in time order).
      const acc = new Map<string, AccidentalMark>();
      for (const t of this.tracks) {
        const track = score.tracks[t];
        if (!track.showStaff) continue;
        const fifths = this.viewKey(t, bar);
        const entries: { onset: number; note: Note; pitch: Pitch; tiedFromPrev: boolean }[] = [];
        for (const vd of voices) {
          if (vd.track !== t) continue;
          vd.beats.forEach((b, i) => {
            if (b.rest) return;
            for (const n of b.notes) entries.push({ onset: vd.onsets[i], note: n, pitch: this.pitchOf(n, t, bar), tiedFromPrev: this.isTiedFromPrev(t, vd.voice, bar, i, n) });
          });
        }
        entries.sort((a, b) => a.onset - b.onset);
        const marks = computeAccidentals(
          entries.map((e) => ({ pitch: e.pitch, tiedFromPrevBar: e.tiedFromPrev && e.onset === 0, tiedFromPrev: e.tiedFromPrev, display: e.note.accidentalDisplay })),
          fifths,
        );
        entries.forEach((e, i) => marks[i] && acc.set(e.note.id, marks[i]));
      }
      const cols: ColMeasure[] = ticksSorted.map((tick, i) => {
        const next = i + 1 < ticksSorted.length ? ticksSorted[i + 1] : length;
        const gap = Math.max(1, next - tick);
        return { tick, gap, pre: 0, post: 1.6 * sp };
      });
      for (const c of cols) shortest = Math.min(shortest, c.gap);
      // Pre/post requirements.
      for (const vd of voices) {
        const track = score.tracks[vd.track];
        let graceRun = 0;
        vd.beats.forEach((b, i) => {
          if (b.grace) {
            graceRun++;
            return;
          }
          const col = cols.find((c) => c.tick === vd.onsets[i]);
          if (!col) return;
          let pre = graceRun * 1.5 * sp;
          graceRun = 0;
          let post = 0;
          const headW = b.duration.base === 1 ? G.noteheadWhole.adv * sp : G.noteheadBlack.adv * sp;
          if (!b.rest && track.showStaff) {
            const accCols = this.accidentalColumns(b, vd.track, bar, acc);
            if (accCols.count) pre += accCols.width + 0.3 * sp;
            const hasSecond = this.hasSecond(b, vd.track, bar);
            post = headW * (hasSecond ? 2 : 1) + b.duration.dots * 0.55 * sp + 0.9 * sp;
            if (b.duration.base >= 8 && !b.grace) post += 0.6 * sp; // flag room
          } else post = headW + 0.9 * sp;
          if (track.showTab && !b.rest) {
            const fretW = Math.max(0, ...b.notes.map((n) => this.measure(this.fretText(n), this.fretFont)));
            post = Math.max(post, fretW + 0.9 * sp);
            pre = Math.max(pre, Math.max(0, fretW - headW) / 2);
          }
          if (b.chord?.name) post = Math.max(post, this.measure(b.chord.name, this.chordFont) + 0.8 * sp);
          if (b.lyrics?.length) post = Math.max(post, Math.max(...b.lyrics.map((l) => this.measure(l, this.lyricFont))) + 0.8 * sp);
          if (b.text?.above) post = Math.max(post, Math.min(this.measure(b.text.above, this.textFont), 12 * sp) * 0.6);
          col.pre = Math.max(col.pre, pre);
          col.post = Math.max(col.post, post);
        });
      }
      const timeChange = bar > 0 && !!info.timeSig && !sameTs(info.timeSig, timeSigAt(score, bar - 1));
      const keyChange = bar > 0 && !!info.keySig && (info.keySig.fifths !== keySigAt(score, bar - 1).fifths || info.keySig.mode !== keySigAt(score, bar - 1).mode);
      const clefChange = new Map<number, Clef>();
      if (bar > 0) {
        for (const t of this.tracks) {
          const tr = score.tracks[t];
          if (tr.clefChanges?.[bar] && tr.clefChanges[bar] !== clefAt(tr, bar - 1)) clefChange.set(t, tr.clefChanges[bar]);
        }
      }
      const bd: BarData = {
        bar,
        info,
        ts,
        capacity,
        length,
        cols,
        voices,
        acc,
        overfull,
        timeChange,
        keyChange,
        clefChange,
        multiRest: info.multiRest && info.multiRest > 1 ? info.multiRest : 0,
      };
      barCache.set(key, bd);
      this.bars.push(bd);
    }
    this.shortestGlobal = Number.isFinite(shortest) ? Math.max(shortest, WHOLE / 32) : WHOLE / 8;
  }

  isTiedFromPrev(track: number, voice: number, bar: number, beatIdx: number, n: Note): boolean {
    const beats = this.score.tracks[track].bars[bar]?.voices[voice] ?? [];
    let prev: Beat | undefined;
    if (beatIdx > 0) prev = beats[beatIdx - 1];
    else if (bar > 0) {
      const pv = this.score.tracks[track].bars[bar - 1]?.voices[voice] ?? [];
      prev = pv[pv.length - 1];
    }
    if (!prev || prev.rest) return false;
    const m = midiOf(n.pitch);
    return prev.notes.some((p) => p.tieToNext && midiOf(p.pitch) === m);
  }

  hasSecond(b: Beat, track: number, bar: number): boolean {
    if (b.notes.length < 2) return false;
    const pos = b.notes.map((n) => diatonicIndex(this.pitchOf(n, track, bar))).sort((a, c) => a - c);
    for (let i = 1; i < pos.length; i++) if (pos[i] - pos[i - 1] === 1) return true;
    return false;
  }

  accidentalColumns(b: Beat, track: number, bar: number, acc: Map<string, AccidentalMark>): { count: number; width: number; colOf: Map<string, number> } {
    const sp = this.sp;
    const items = b.notes
      .filter((n) => acc.get(n.id))
      .map((n) => ({ id: n.id, pos: diatonicIndex(this.pitchOf(n, track, bar)) }))
      .sort((a, c) => c.pos - a.pos);
    const colsLast: number[] = [];
    const colOf = new Map<string, number>();
    for (const it of items) {
      let c = 0;
      while (c < colsLast.length && colsLast[c] - it.pos < 6) c++;
      colsLast[c] = it.pos;
      colOf.set(it.id, c);
    }
    return { count: colsLast.length, width: colsLast.length * 1.15 * sp, colOf };
  }

  fretText(n: Note): string {
    if (n.dead) return 'X';
    const f = String(n.fret ?? '?');
    if (n.harmonic) return `<${f}>`;
    if (n.ghost || n.head === 'ghost') return `(${f})`;
    return f;
  }

  spacing(gap: number, shortest: number): number {
    return this.sp * (2.4 + 1.6 * Math.log2(Math.max(gap, shortest) / shortest));
  }

  barPrefixWidth(bd: BarData, firstInSystem: boolean): number {
    const sp = this.sp;
    let w = 0;
    if (!firstInSystem) {
      if (bd.clefChange.size) w += 2.6 * sp;
      if (bd.keyChange) w += this.keyWidth(bd.bar) + 0.8 * sp;
      if (bd.timeChange) w += 2.6 * sp;
    }
    if (bd.info.repeatStart) w += 1.3 * sp;
    return w;
  }

  barWidth(bd: BarData, shortest: number, firstInSystem: boolean): number {
    const sp = this.sp;
    if (bd.multiRest) return 14 * sp;
    let w = 1.3 * sp + this.barPrefixWidth(bd, firstInSystem);
    for (const c of bd.cols) w += c.pre + Math.max(this.spacing(c.gap, shortest), c.post);
    w += 0.4 * sp;
    if (bd.info.repeatEnd) w += 1.0 * sp;
    return w;
  }

  keyWidth(bar: number): number {
    let max = 0;
    for (const t of this.tracks) {
      if (!this.score.tracks[t].showStaff) continue;
      max = Math.max(max, Math.abs(this.viewKey(t, bar)));
    }
    let w = max * 1.05 * this.sp;
    // Cancellation naturals when changing to C.
    if (max === 0 && bar > 0) {
      for (const t of this.tracks) {
        if (!this.score.tracks[t].showStaff) continue;
        w = Math.max(w, Math.abs(this.viewKey(t, bar - 1)) * 0.85 * this.sp);
      }
    }
    return w;
  }

  /** x where staff lines start (after instrument labels and the bracket). */
  staffStart(sys: SystemBox): number {
    return sys.x0 + this.instrumentLabelWidth(sys.index === 0) + (this.tracks.length > 1 ? 1.2 * this.sp : 0);
  }

  instrumentLabelWidth(firstSystem: boolean): number {
    if (this.tracks.length < 2) return 0;
    let w = 0;
    for (const t of this.tracks) {
      const tr = this.score.tracks[t];
      const label = firstSystem ? tr.name : tr.shortName ?? '';
      if (label) w = Math.max(w, this.measure(label, this.smallFont));
    }
    return w ? w + 1.2 * this.sp : 0;
  }

  headerWidth(bar: number, firstSystem: boolean): number {
    const sp = this.sp;
    const anyStaff = this.tracks.some((t) => this.score.tracks[t].showStaff);
    let w = this.instrumentLabelWidth(firstSystem) + (this.tracks.length > 1 ? 1.2 * sp : 0);
    w += 0.8 * sp + 2.8 * sp; // clef
    if (anyStaff) {
      const kw = this.keyWidth(bar);
      if (kw) w += kw + 1.0 * sp;
    }
    const showTime = firstSystem || this.bars[bar]?.timeChange;
    if (showTime) w += 2.6 * sp;
    return w + 0.4 * sp;
  }

  // ------------------------------------------------------------ system construction
  snapshotPending(): PendingSnapshot {
    return {
      ties: [...this.pendingTies].map(([k, v]) => [k, { x: v.x, y: v.y, up: v.up, id: v.id }]),
      slurs: [...this.pendingSlurs].map(([k, v]) => [k, { x: v.x, y: v.y, above: v.above, id: v.id }]),
      legato: [...this.pendingLegato].map(([k, v]) => [k, { x: v.x, y: v.y, midi: v.midi, kind: v.kind, id: v.id }]),
    };
  }

  restorePending(p: PendingSnapshot, system: number): void {
    this.pendingTies = new Map(p.ties.map(([k, v]) => [k, { ...v, system }]));
    this.pendingSlurs = new Map(p.slurs.map(([k, v]) => [k, { ...v, system }]));
    this.pendingLegato = new Map(p.legato.map(([k, v]) => [k, { ...v, system }]));
  }

  buildSystem(index: number, barIdxs: number[], marginL: number, avail: number, isLast: boolean): SystemBox {
    const last = barIdxs[barIdxs.length - 1];
    const pendIn = [...this.pendingTies.entries()].map(([k, v]) => `${k}${v.up ? '^' : 'v'}`).join(';') + '#' + [...this.pendingSlurs.entries()].map(([k, v]) => `${k}${v.above ? '^' : 'v'}`).join(';');
    const tempos = JSON.stringify(this.score.tempos.filter((t) => t.bar >= barIdxs[0] && t.bar <= last));
    const key = [
      this.optsKey,
      this.trackSig,
      index === 0 ? 'first' : 'next',
      isLast ? 'last' : '',
      avail,
      marginL,
      barIdxs.map((b) => this.barKey(b)).join('~'),
      this.barNumbers[barIdxs[0]],
      oid(this.score.bars[last + 1]),
      this.score.bars.length,
      tempos,
      pendIn,
    ].join('\u0001');
    const hit = sysCache.get(key);
    if (hit) {
      const sys: SystemBox = { ...hit.sys, index, bands: hit.sys.bands.map((b) => ({ ...b })) };
      for (const bb of hit.barBoxes) this.layoutBars.push({ ...bb, system: index });
      const list: BeatBox[] = [];
      for (const bb of hit.beatBoxes) {
        const c = { ...bb, system: index, noteYs: bb.noteYs.slice(), tabYs: bb.tabYs.slice() };
        this.beats.push(c);
        list.push(c);
      }
      this.systemBeats.set(index, list);
      this.restorePending(hit.pendingOut, index);
      return sys;
    }
    const built = this.buildSystemFresh(index, barIdxs, marginL, avail, isLast);
    const beatBoxes = (this.systemBeats.get(index) ?? []).map((bb) => ({ ...bb, noteYs: bb.noteYs.slice(), tabYs: bb.tabYs.slice() }));
    const barBoxes = this.layoutBars.filter((b) => b.system === index).map((b) => ({ ...b }));
    sysCache.set(key, { sys: { ...built, bands: built.bands.map((b) => ({ ...b })) }, barBoxes, beatBoxes, pendingOut: this.snapshotPending() });
    return built;
  }

  buildSystemFresh(index: number, barIdxs: number[], marginL: number, avail: number, isLast: boolean): SystemBox {
    const { sp } = this;
    this.curIds = new Map();
    this.beatBoxCache = new Map();
    const firstSystem = index === 0;
    const firstBar = barIdxs[0];
    // Per-system shortest note for spacing.
    let shortest = Infinity;
    for (const b of barIdxs) for (const c of this.bars[b].cols) shortest = Math.min(shortest, c.gap);
    shortest = Number.isFinite(shortest) ? Math.max(shortest, WHOLE / 32) : this.shortestGlobal;
    const headerW = this.headerWidth(firstBar, firstSystem);
    const natural = barIdxs.reduce((s, b, i) => s + this.barWidth(this.bars[b], shortest, i === 0), headerW);
    let springTotal = 0;
    for (const b of barIdxs) {
      const bd = this.bars[b];
      if (bd.multiRest) springTotal += 14 * sp;
      else for (const c of bd.cols) springTotal += Math.max(this.spacing(c.gap, shortest), c.post);
    }
    let extra = Number.isFinite(avail) ? avail - natural : 0;
    if (isLast && natural < avail * 0.7) extra = 0;
    if (extra < 0) extra = Math.max(extra, -springTotal * 0.25);
    const stretch = springTotal > 0 ? 1 + extra / springTotal : 1;

    // Horizontal positions.
    const x0 = marginL;
    let x = x0 + headerW;
    const barBoxes: BarBox[] = [];
    barIdxs.forEach((b, i) => {
      const bd = this.bars[b];
      const bx0 = x;
      x += this.barPrefixWidth(bd, i === 0);
      const contentX = x;
      x += 1.3 * sp;
      const columns: Column[] = [];
      if (bd.multiRest) {
        columns.push({ tick: 0, x: bx0 + 7 * sp });
        x = bx0 + 14 * sp * stretch;
      } else {
        for (const c of bd.cols) {
          x += c.pre;
          columns.push({ tick: c.tick, x });
          x += Math.max(this.spacing(c.gap, shortest), c.post) * stretch;
        }
        x += 0.4 * sp;
        if (bd.info.repeatEnd) x += 1.0 * sp;
      }
      barBoxes.push({ bar: b, system: index, x0: bx0, x1: x, contentX, columns, length: bd.length });
    });
    const x1 = x;
    this.layoutBars.push(...barBoxes);

    const sys: SystemBox = { index, y0: 0, y1: 0, top: 0, bottom: 0, x0, x1, bars: barIdxs, bands: [], items: [], page: 0, dy: 0, ids: this.curIds };

    // Bands: generate band-local items then stack vertically.
    let y = 0;
    let prevBelow = 0;
    let firstBandAbove = 0;
    const bandItems: { band: Band; items: DrawItem[] }[] = [];
    this.tracks.forEach((t, ti) => {
      const track = this.score.tracks[t];
      const kinds: ('staff' | 'tab')[] = [];
      if (track.showStaff) kinds.push('staff');
      if (track.showTab && track.tuning?.length) kinds.push('tab');
      if (!kinds.length) kinds.push('staff');
      kinds.forEach((kind, ki) => {
        const isTop = ki === 0;
        const isBottom = ki === kinds.length - 1;
        const res =
          kind === 'staff'
            ? this.staffBand(sys, barBoxes, t, isTop, isBottom, firstSystem, ti === 0)
            : this.tabBand(sys, barBoxes, t, isTop, isBottom, ti === 0);
        const height = kind === 'staff' ? 4 * sp : (track.tuning!.length - 1) * TAB_GAP * sp;
        const above = Math.max(kind === 'staff' ? 2.5 * sp : 1.6 * sp, -res.minY + 0.6 * sp);
        const below = Math.max(kind === 'staff' ? 2.5 * sp : 1.6 * sp, res.maxY - height + 0.6 * sp);
        if (bandItems.length === 0) {
          firstBandAbove = above;
          y = above;
        } else {
          y += prevBelow + above + (isTop ? 1.5 * sp : 0);
        }
        const band: Band = { track: t, kind, top: y, bottom: y + height, gap: kind === 'staff' ? sp : TAB_GAP * sp, lines: kind === 'staff' ? STAFF_LINES : track.tuning!.length };
        for (const it of res.items) shiftItem(it, 0, y);
        for (const bb of res.beatBoxes) {
          if (kind === 'staff') bb.noteYs = bb.noteYs.map((v) => v + y);
          else bb.tabYs = bb.tabYs.map((v) => v + y);
        }
        bandItems.push({ band, items: res.items });
        y += height;
        prevBelow = below;
      });
    });
    sys.y0 = 0;
    sys.top = bandItems[0].band.top;
    sys.bottom = bandItems[bandItems.length - 1].band.bottom;
    sys.y1 = sys.bottom + prevBelow;
    void firstBandAbove;
    sys.bands = bandItems.map((b) => b.band);
    for (const b of bandItems) sys.items.push(...b.items);

    // System-level items: barlines, brackets, labels, bar numbers.
    this.systemItems(sys, barBoxes, firstSystem, headerW);
    // Merge beat boxes for this system (staff + tab results share BeatBox objects).
    return sys;
  }

  // Beat boxes are shared between the staff and tab bands of the same track.
  beatBoxCache = new Map<string, BeatBox>();
  systemBeats = new Map<number, BeatBox[]>();
  beatBox(sys: SystemBox, ref: { track: number; bar: number; voice: number; beat: number }, b: Beat, x: number, w: number, xEnd: number, tick: number): BeatBox {
    let bb = this.beatBoxCache.get(b.id);
    if (!bb) {
      bb = { ref: { ...ref }, beatId: b.id, system: sys.index, x, w, xEnd, tick, rest: b.rest, noteYs: [], tabYs: [] };
      this.beatBoxCache.set(b.id, bb);
      this.beats.push(bb);
      const list = this.systemBeats.get(sys.index);
      if (list) list.push(bb);
      else this.systemBeats.set(sys.index, [bb]);
    }
    return bb;
  }

  add(items: DrawItem[], it: DrawItem): DrawItem {
    items.push(it);
    if (it.id) {
      const list = this.curIds.get(it.id);
      if (list) list.push(it);
      else this.curIds.set(it.id, [it]);
    }
    return it;
  }

  noteColor(n: Note, pitch: Pitch): string | undefined {
    const mode = this.opts.noteColors ?? 'authored';
    if (mode === 'hide') return undefined;
    if (mode === 'boomwhackers') return BOOMWHACKERS[mod(midiOf(pitch), 12)];
    return n.color;
  }

  // ------------------------------------------------------------ staff band
  staffBand(sys: SystemBox, barBoxes: BarBox[], t: number, isTop: boolean, isBottom: boolean, firstSystem: boolean, topTrack: boolean) {
    const { sp, ink } = this;
    const items: DrawItem[] = [];
    const track = this.score.tracks[t];
    const yOf = (pos: number) => 2 * sp - pos * (sp / 2);
    const lw = ENGRAVING.staffLineThickness * sp;
    let minY = 0;
    let maxY = 4 * sp;
    const ext = (y: number) => {
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    };
    const beatBoxes: BeatBox[] = [];

    // Staff lines.
    const lx0 = this.staffStart(sys);
    for (let i = 0; i < STAFF_LINES; i++) {
      this.add(items, { k: 'line', x1: lx0, y1: i * sp, x2: sys.x1, y2: i * sp, w: lw, color: '#333' });
    }

    // System header: clef, key, time.
    const firstBar = barBoxes[0].bar;
    let hx = sys.x0 + this.instrumentLabelWidth(firstSystem) + (this.tracks.length > 1 ? 1.2 * sp : 0) + 0.8 * sp;
    const clef = clefAt(track, firstBar);
    hx = this.drawClef(items, clef, hx, false);
    const fifths = this.viewKey(t, firstBar);
    hx = this.drawKey(items, fifths, clef, hx + 0.2 * sp, null);
    const showTime = firstSystem || this.bars[firstBar].timeChange;
    // A full space after a key signature; straight after the clef, less.
    const timeX = hx + (fifths !== 0 && clef !== 'percussion' ? 1.0 : 0.6) * sp;
    if (showTime) this.drawTime(items, this.bars[firstBar].ts, timeX, 4 * sp);

    const posOf = (p: Pitch, bar: number) => diatonicIndex(p) - diatonicIndex(middleLineOf(clefAt(track, bar)));

    for (const bbx of barBoxes) {
      const bd = this.bars[bbx.bar];
      const bar = bbx.bar;
      // Mid-system prefix: clef / key / time changes, repeat start.
      let px = bbx.x0 + 0.5 * sp;
      if (bbx !== barBoxes[0]) {
        const cc = bd.clefChange.get(t);
        if (cc) px = this.drawClef(items, cc, px, true) + 0.3 * sp;
        else if (bd.clefChange.size) px += 2.6 * sp;
        if (bd.keyChange) {
          const prevF = this.viewKey(t, bar - 1);
          px = this.drawKey(items, this.viewKey(t, bar), clefAt(track, bar), px, prevF) + 0.6 * sp;
        }
        if (bd.timeChange) {
          this.drawTime(items, bd.ts, px + 0.3 * sp, 4 * sp);
          px += 2.6 * sp;
        }
      }
      if (bd.overfull) this.add(items, { k: 'rect', x: bbx.x0, y: -0.5 * sp, w: bbx.x1 - bbx.x0, h: 5 * sp, fill: 'rgba(239,106,76,0.08)' });
      if (bd.multiRest) {
        this.drawMultiRest(items, bbx, bd.multiRest);
        continue;
      }
      if (bd.info.simile) {
        const g = bd.info.simile === 2 ? G.repeat2Bars : G.repeat1Bar;
        this.add(items, { k: 'glyph', x: (bbx.x0 + bbx.x1) / 2 - (g.adv * sp) / 2, y: 2 * sp, ch: g.ch, size: this.fontSize, color: ink });
        continue;
      }
      const voicesHere = bd.voices.filter((v) => v.track === t);
      const multiVoice = voicesHere.filter((v) => v.beats.length > 0).length > 1;
      for (const vd of voicesHere) {
        if (vd.beats.length === 0) continue;
        this.engraveVoice(sys, items, bbx, bd, vd, t, posOf, yOf, multiVoice, ext, beatBoxes);
      }
    }

    // ---- decorations above the staff (system-wide rows) ----
    if (isTop) {
      let rowTop = Math.min(-1.2 * sp, minY - 1.0 * sp);
      rowTop = this.aboveRows(items, sys, barBoxes, t, rowTop, topTrack, ext, showTime ? timeX : undefined);
      minY = Math.min(minY, rowTop);
    }
    // ---- below the staff: dynamics, hairpins, inner-below text ----
    this.belowRows(items, sys, barBoxes, t, Math.max(6 * sp, maxY + 1.6 * sp), isBottom, ext);

    return { items, minY, maxY, beatBoxes };
  }

  drawClef(items: DrawItem[], clef: Clef, x: number, small: boolean): number {
    const { sp } = this;
    const size = small ? this.fontSize * 0.75 : this.fontSize;
    const map: Record<Clef, [keyof typeof G, number]> = {
      treble: ['gClef', 3],
      treble8vb: ['gClef8vb', 3],
      bass: ['fClef', 1],
      alto: ['cClef', 2],
      tenor: ['cClef', 1],
      percussion: ['unpitchedPercussionClef1', 2],
    };
    const [name, line] = map[clef];
    const g = G[name];
    this.add(items, { k: 'glyph', x, y: line * sp, ch: g.ch, size, color: this.ink });
    return x + g.adv * sp * (small ? 0.75 : 1) + 0.6 * sp;
  }

  drawKey(items: DrawItem[], fifths: number, clef: Clef, x: number, prevFifths: number | null): number {
    const { sp } = this;
    const yOf = (pos: number) => 2 * sp - pos * (sp / 2);
    if (fifths === 0 && prevFifths !== null && prevFifths !== 0) {
      // Cancel the previous key with naturals.
      const kp = keyPositions(prevFifths, clef);
      for (const p of kp.pos) {
        this.add(items, { k: 'glyph', x, y: yOf(p), ch: G.accidentalNatural.ch, size: this.fontSize, color: this.ink });
        x += 0.85 * sp;
      }
      return x;
    }
    if (clef === 'percussion') return x;
    const kp = keyPositions(fifths, clef);
    for (const p of kp.pos) {
      this.add(items, { k: 'glyph', x, y: yOf(p), ch: kp.glyph, size: this.fontSize, color: this.ink });
      x += 1.05 * sp;
    }
    return x;
  }

  drawTime(items: DrawItem[], ts: TimeSig, x: number, height: number): void {
    const { sp } = this;
    if (ts.symbol === 'common' || ts.symbol === 'cut') {
      const g = ts.symbol === 'common' ? G.timeSigCommon : G.timeSigCutCommon;
      this.add(items, { k: 'glyph', x, y: height / 2, ch: g.ch, size: this.fontSize, color: this.ink });
      return;
    }
    const digits = (n: number) =>
      String(n)
        .split('')
        .map((d) => G[`timeSig${d}` as keyof typeof G].ch)
        .join('');
    const num = digits(ts.num);
    const den = digits(ts.den);
    const w = (s: string) => s.length * 1.75 * sp;
    const cw = Math.max(w(num), w(den));
    const size = height < 4 * sp ? this.fontSize * (height / (4 * sp)) : this.fontSize;
    this.add(items, { k: 'glyph', x: x + (cw - w(num)) / 2, y: height / 4, ch: num, size, color: this.ink });
    this.add(items, { k: 'glyph', x: x + (cw - w(den)) / 2, y: (3 * height) / 4, ch: den, size, color: this.ink });
  }

  drawMultiRest(items: DrawItem[], bbx: BarBox, count: number): void {
    const { sp } = this;
    const xa = bbx.x0 + 2 * sp;
    const xb = bbx.x1 - 2 * sp;
    this.add(items, { k: 'rect', x: xa, y: 1.5 * sp, w: xb - xa, h: sp, fill: this.ink });
    this.add(items, { k: 'line', x1: xa, y1: 1 * sp, x2: xa, y2: 3 * sp, w: 0.16 * sp, color: this.ink });
    this.add(items, { k: 'line', x1: xb, y1: 1 * sp, x2: xb, y2: 3 * sp, w: 0.16 * sp, color: this.ink });
    const digits = String(count)
      .split('')
      .map((d) => G[`timeSig${d}` as keyof typeof G].ch)
      .join('');
    this.add(items, { k: 'glyph', x: (xa + xb) / 2 - (digits.length * 1.75 * sp) / 2, y: -1.5 * sp, ch: digits, size: this.fontSize, color: this.ink });
  }

  // ------------------------------------------------------------ one voice of one bar on a staff
  engraveVoice(
    sys: SystemBox,
    items: DrawItem[],
    bbx: BarBox,
    bd: BarData,
    vd: VoiceData,
    t: number,
    posOf: (p: Pitch, bar: number) => number,
    yOf: (pos: number) => number,
    multiVoice: boolean,
    ext: (y: number) => void,
    beatBoxes: BeatBox[],
  ): void {
    const { sp, ink } = this;
    const bar = bbx.bar;
    const track = this.score.tracks[t];
    const colX = (tick: number) => bbx.columns.find((c) => c.tick === tick)?.x ?? bbx.columns[0].x;
    const colEnd = (tick: number) => {
      const i = bbx.columns.findIndex((c) => c.tick === tick);
      return i >= 0 && i + 1 < bbx.columns.length ? bbx.columns[i + 1].x : bbx.x1;
    };
    const voiceDir: 1 | -1 | null = multiVoice ? (vd.voice % 2 === 0 ? 1 : -1) : null;
    const groups = beamGroups(vd.beats, vd.onsets, bd.ts);
    const groupOf = new Map<number, number[]>();
    for (const g of groups) if (g.length > 1) for (const i of g) groupOf.set(i, g);
    const geoms: (BeatGeom | null)[] = vd.beats.map(() => null);
    const accCols = new Map<number, ReturnType<Engraver['accidentalColumns']>>();

    for (const r of vd.implied ?? []) {
      const g = G[REST_GLYPH[r.base] ?? 'restQuarter'];
      let ry = 2 * sp;
      if (voiceDir === 1) ry -= 2 * sp;
      if (voiceDir === -1) ry += 2 * sp;
      const rx = colX(r.tick);
      this.add(items, { k: 'glyph', x: rx, y: ry, ch: g.ch, size: this.fontSize, color: IMPLIED_REST_INK });
      for (let d = 0; d < r.dots; d++) this.add(items, { k: 'glyph', x: rx + g.adv * sp + 0.3 * sp + d * 0.5 * sp, y: ry - 0.5 * sp, ch: G.augmentationDot.ch, size: this.fontSize, color: IMPLIED_REST_INK });
      ext(ry - g.ne[1] * sp);
      ext(ry - g.sw[1] * sp);
    }

    // Pass 1: noteheads, rests, accidentals, dots, ledger lines.
    const fullBarRest = vd.beats.length === 1 && vd.beats[0].rest && durTicks(vd.beats[0].duration) >= bd.capacity;
    let graceQueue: number[] = [];
    vd.beats.forEach((b, i) => {
      if (b.grace) {
        graceQueue.push(i);
        return;
      }
      const x = colX(vd.onsets[i]);
      const headGlyph = this.headGlyph(b, b.notes[0]);
      const headW = headGlyph.adv * sp * (b.small ? 0.75 : 1);
      const bb = this.beatBox(sys, { track: t, bar, voice: vd.voice, beat: i }, b, x, headW, colEnd(vd.onsets[i]), vd.onsets[i]);
      beatBoxes.push(bb);
      if (graceQueue.length) {
        this.drawGraces(items, graceQueue.map((gi) => vd.beats[gi]), x, t, bar, posOf, yOf, ext, sys, vd, graceQueue, bd);
        graceQueue = [];
      }
      if (b.rest) {
        if (b.hidden) return;
        let rx = x;
        let base = b.duration.base;
        if (fullBarRest) {
          base = 1;
          rx = (bbx.contentX + bbx.x1) / 2 - (G.restWhole.adv * sp) / 2;
          bb.x = rx;
        }
        const g = G[REST_GLYPH[base] ?? 'restQuarter'];
        let ry = base === 1 ? 1 * sp : 2 * sp;
        if (voiceDir === 1) ry -= 2 * sp;
        if (voiceDir === -1) ry += 2 * sp;
        this.add(items, { k: 'glyph', x: rx, y: ry, ch: g.ch, size: this.fontSize * (b.small ? 0.75 : 1), color: b.color ?? ink, id: b.id });
        ext(ry - g.ne[1] * sp);
        ext(ry - g.sw[1] * sp);
        for (let d = 0; d < b.duration.dots; d++) {
          this.add(items, { k: 'glyph', x: rx + g.adv * sp + 0.3 * sp + d * 0.5 * sp, y: ry - 0.5 * sp, ch: G.augmentationDot.ch, size: this.fontSize, color: ink, id: b.id });
        }
        bb.noteYs = [ry];
        return;
      }
      // Note positions.
      const notes: NoteGeom[] = b.notes.map((n) => {
        const pitch = this.pitchOf(n, t, bar);
        const pos = posOf(pitch, bar);
        return { note: n, pitch, pos, x, y: yOf(pos), displaced: false };
      });
      // Stem direction.
      let dir: 1 | -1 = b.stem === 'up' ? 1 : b.stem === 'down' ? -1 : voiceDir ?? autoStemDir(notes.map((n) => n.pos));
      const grp = groupOf.get(i);
      if (grp && b.stem !== 'up' && b.stem !== 'down' && voiceDir === null) {
        const all: number[] = [];
        for (const gi of grp) for (const n of vd.beats[gi].notes) all.push(posOf(this.pitchOf(n, t, bar), bar));
        const hi = Math.max(...all);
        const lo = Math.min(...all);
        dir = hi + lo >= 0 ? -1 : 1;
      }
      // Seconds: displace noteheads to the other side of the stem.
      const sorted = [...notes].sort((a, c) => (dir === 1 ? a.pos - c.pos : c.pos - a.pos));
      for (let k = 1; k < sorted.length; k++) {
        if (Math.abs(sorted[k].pos - sorted[k - 1].pos) === 1 && !sorted[k - 1].displaced) {
          sorted[k].displaced = true;
          sorted[k].x = x + (dir === 1 ? headW - 0.12 * sp : -(headW - 0.12 * sp));
        }
      }
      const ac = this.accidentalColumns(b, t, bar, bd.acc);
      accCols.set(i, ac);
      const anyLeft = notes.some((n) => n.displaced && dir === -1);
      const accBaseX = x - (anyLeft ? headW : 0) - 0.25 * sp;
      for (const ng of notes) {
        const color = this.noteColor(ng.note, ng.pitch) ?? (ng.note.hidden ? '#bbb' : ink);
        // Ledger lines.
        this.ledgers(items, ng.pos, ng.x, headW, b.id);
        const hg = this.headGlyph(b, ng.note);
        this.add(items, { k: 'glyph', x: ng.x, y: ng.y, ch: hg.ch, size: this.fontSize * (b.small ? 0.75 : 1), color, id: ng.note.id });
        if (ng.note.ghost || ng.note.head === 'ghost') {
          this.add(items, { k: 'glyph', x: ng.x - 0.45 * sp, y: ng.y, ch: G.noteheadParenthesisLeft.ch, size: this.fontSize, color, id: ng.note.id });
          this.add(items, { k: 'glyph', x: ng.x + headW + 0.1 * sp, y: ng.y, ch: G.noteheadParenthesisRight.ch, size: this.fontSize, color, id: ng.note.id });
        }
        ext(ng.y - 0.6 * sp);
        ext(ng.y + 0.6 * sp);
        // Accidental.
        const mark = bd.acc.get(ng.note.id);
        if (mark && !ng.note.hidden) {
          const gname = ACC_GLYPH[mark.alter];
          const g = G[gname];
          const col = ac.colOf.get(ng.note.id) ?? 0;
          let ax = accBaseX - g.adv * sp - col * 1.15 * sp;
          if (mark.cautionary) {
            ax -= 0.6 * sp;
            this.add(items, { k: 'glyph', x: ax - 0.1 * sp, y: ng.y, ch: G.accidentalParensLeft.ch, size: this.fontSize, color, id: ng.note.id });
            this.add(items, { k: 'glyph', x: ax + 0.5 * sp, y: ng.y, ch: g.ch, size: this.fontSize, color, id: ng.note.id });
            this.add(items, { k: 'glyph', x: ax + 0.5 * sp + g.adv * sp, y: ng.y, ch: G.accidentalParensRight.ch, size: this.fontSize, color, id: ng.note.id });
          } else {
            this.add(items, { k: 'glyph', x: ax, y: ng.y, ch: g.ch, size: this.fontSize, color, id: ng.note.id });
          }
        }
        // Note names.
        const names = this.opts.noteNames ?? 'none';
        if (names !== 'none' && b.duration.base >= 4) {
          const label = noteLabel(ng.pitch, names, { fifths: this.viewKey(t, bar), mode: keySigAt(this.score, bar).mode });
          this.add(items, { k: 'text', x: ng.x + headW / 2, y: ng.y + 0.05 * sp, text: label, font: `700 ${(0.62 * sp * (label.length > 2 ? 0.8 : 1)).toFixed(1)}px Inter, Arial, sans-serif`, color: '#fff', align: 'center', baseline: 'middle', id: ng.note.id });
        }
      }
      // Dots.
      if (b.duration.dots) {
        const right = Math.max(...notes.map((n) => n.x)) + headW;
        for (const ng of notes) {
          const dy = ng.pos % 2 === 0 ? -sp / 2 : 0;
          for (let d = 0; d < b.duration.dots; d++) {
            this.add(items, { k: 'glyph', x: right + 0.35 * sp + d * 0.5 * sp, y: ng.y + dy, ch: G.augmentationDot.ch, size: this.fontSize, color: ink, id: ng.note.id });
          }
        }
      }
      // Stem geometry (unbeamed default).
      const top = Math.min(...notes.map((n) => n.y));
      const bottom = Math.max(...notes.map((n) => n.y));
      const level = beamCount(b.duration.base);
      const len = (3.5 + Math.max(0, level - 2) * 0.75) * sp;
      const stemX = dir === 1 ? x + headW - (ENGRAVING.stemThickness * sp) / 2 : x + (ENGRAVING.stemThickness * sp) / 2;
      let stemTop: number;
      let stemBottom: number;
      if (dir === 1) {
        stemBottom = bottom - 0.168 * sp;
        stemTop = Math.min(top - len, 2 * sp); // reach the middle line
        if (top - len > 2 * sp && top > 4 * sp) stemTop = 2 * sp;
        else stemTop = top - len;
        if (bottom > 6 * sp) stemTop = Math.min(stemTop, 2 * sp);
      } else {
        stemTop = top + 0.168 * sp;
        stemBottom = bottom + len;
        if (top < -2 * sp) stemBottom = Math.max(stemBottom, 2 * sp);
      }
      geoms[i] = { beat: b, vd, idx: i, x, headW, notes, stemDir: dir, stemX, stemTop, stemBottom, beamed: !!grp };
      bb.noteYs = b.notes.map((n) => notes.find((g) => g.note === n)!.y);
    });

    // Pass 2: beams.
    for (const g of groups) {
      if (g.length < 2) continue;
      this.drawBeamGroup(items, g.map((i) => geoms[i]!).filter(Boolean), ext);
    }
    // Pass 3: stems + flags for unbeamed notes, articulations, tremolo, ties, slurs.
    vd.beats.forEach((b, i) => {
      const gm = geoms[i];
      if (!gm || b.rest) return;
      const color = b.color ?? ink;
      const stemW = ENGRAVING.stemThickness * sp;
      if (b.duration.base >= 2 && b.stem !== 'hidden') {
        this.add(items, { k: 'line', x1: gm.stemX, y1: gm.stemTop, x2: gm.stemX, y2: gm.stemBottom, w: stemW, color, id: b.id });
        ext(gm.stemTop);
        ext(gm.stemBottom);
        if (!gm.beamed && b.duration.base >= 8) {
          const lvl = beamCount(b.duration.base);
          const name = `flag${['8th', '16th', '32nd', '64th'][lvl - 1]}${gm.stemDir === 1 ? 'Up' : 'Down'}` as keyof typeof G;
          const fg = G[name];
          this.add(items, { k: 'glyph', x: gm.stemX - stemW / 2, y: gm.stemDir === 1 ? gm.stemTop : gm.stemBottom, ch: fg.ch, size: this.fontSize, color, id: b.id });
        }
        if (b.tremolo) {
          const tg = G[`tremolo${b.tremolo}` as keyof typeof G];
          const ty = gm.stemDir === 1 ? gm.stemTop + 1.6 * sp : gm.stemBottom - 1.6 * sp;
          this.add(items, { k: 'glyph', x: gm.stemX, y: ty, ch: tg.ch, size: this.fontSize, color, id: b.id });
        }
      } else if (b.tremolo) {
        const tg = G[`tremolo${b.tremolo}` as keyof typeof G];
        this.add(items, { k: 'glyph', x: gm.x + gm.headW / 2, y: Math.min(...gm.notes.map((n) => n.y)) - 2 * sp, ch: tg.ch, size: this.fontSize, color, id: b.id });
      }
      this.drawArticulations(items, gm, ext);
      this.drawArpeggio(items, gm, accCols.get(i)?.width ?? 0);
      this.drawTiesAndSlurs(items, sys, gm, t, bbx);
    });

    // Tuplets.
    for (const tg of tupletGroups(vd.beats)) {
      const gs = tg.map((i) => geoms[i]).filter(Boolean) as BeatGeom[];
      const all = tg.map((i) => vd.beats[i]);
      const t0 = all[0].duration.tuplet!;
      if (!gs.length && !all.length) continue;
      const xs = tg.map((i) => colX(vd.onsets[i]));
      const xa = Math.min(...xs);
      const xb = Math.max(...xs) + G.noteheadBlack.adv * sp;
      const up = gs.length ? gs[0].stemDir === 1 : true;
      const ys = gs.map((g) => (up ? g.stemTop : g.stemBottom));
      const yy = up ? Math.min(-0.5 * sp, ...ys) - 1.2 * sp : Math.max(4.5 * sp, ...ys) + 1.2 * sp;
      const fullyBeamed = gs.length === tg.length && gs.every((g) => g.beamed) && groups.some((g) => tg.every((i) => g.includes(i)));
      const label = String(t0.actual)
        .split('')
        .map((d) => G[`tuplet${d}` as keyof typeof G].ch)
        .join('');
      const cx = (xa + xb) / 2;
      if (!fullyBeamed) {
        const hook = up ? 0.6 * sp : -0.6 * sp;
        this.add(items, { k: 'path', pts: [xa, yy + hook, xa, yy, cx - 0.9 * sp, yy], w: 0.16 * sp, color: ink });
        this.add(items, { k: 'path', pts: [cx + 0.9 * sp, yy, xb, yy, xb, yy + hook], w: 0.16 * sp, color: ink });
      }
      this.add(items, { k: 'glyph', x: cx - 0.6 * sp, y: yy + 0.7 * sp, ch: label, size: this.fontSize * 0.9, color: ink });
      ext(yy - 1 * sp);
      ext(yy + 1 * sp);
    }
    void track;
  }

  headGlyph(b: Beat, n?: Note) {
    const base = b.duration.base;
    const head = n?.head ?? 'normal';
    if (head === 'x' || n?.dead) return base === 1 ? G.noteheadXWhole : base === 2 ? G.noteheadXHalf : G.noteheadXBlack;
    if (head === 'diamond' || n?.harmonic) return base <= 1 ? G.noteheadDiamondWhole : base === 2 ? G.noteheadDiamondHalf : G.noteheadDiamondBlack;
    if (head === 'slash') return base === 1 ? G.noteheadSlashWhiteWhole : base === 2 ? G.noteheadSlashWhiteHalf : G.noteheadSlashVerticalEnds;
    if (head === 'circleX') return G.noteheadCircleX;
    return base === 1 ? G.noteheadWhole : base === 2 ? G.noteheadHalf : G.noteheadBlack;
  }

  ledgers(items: DrawItem[], pos: number, x: number, headW: number, id: string): void {
    const { sp } = this;
    const yOf = (p: number) => 2 * sp - p * (sp / 2);
    const w = ENGRAVING.legerLineThickness * sp;
    const ext = ENGRAVING.legerLineExtension * sp;
    for (let p = 6; p <= pos; p += 2) this.add(items, { k: 'line', x1: x - ext, y1: yOf(p), x2: x + headW + ext, y2: yOf(p), w, color: this.ink, id });
    for (let p = -6; p >= pos; p -= 2) this.add(items, { k: 'line', x1: x - ext, y1: yOf(p), x2: x + headW + ext, y2: yOf(p), w, color: this.ink, id });
  }

  drawGraces(
    items: DrawItem[],
    graces: Beat[],
    principalX: number,
    t: number,
    bar: number,
    posOf: (p: Pitch, bar: number) => number,
    yOf: (pos: number) => number,
    ext: (y: number) => void,
    sys: SystemBox,
    vd: VoiceData,
    idxs: number[],
    bd: BarData,
  ): void {
    const { sp, ink } = this;
    const scale = 0.65;
    const headW = G.noteheadBlack.adv * sp * scale;
    let x = principalX - graces.length * 1.5 * sp - 0.2 * sp;
    graces.forEach((b, k) => {
      const bb = this.beatBox(sys, { track: t, bar, voice: vd.voice, beat: idxs[k] }, b, x, headW, x + 1.5 * sp, vd.onsets[idxs[k]]);
      const ys: number[] = [];
      for (const n of b.notes) {
        const pitch = this.pitchOf(n, t, bar);
        const pos = posOf(pitch, bar);
        const y = yOf(pos);
        ys.push(y);
        this.ledgers(items, pos, x, headW, n.id);
        this.add(items, { k: 'glyph', x, y, ch: G.noteheadBlack.ch, size: this.fontSize * scale, color: ink, id: n.id });
        const mark = bd.acc.get(n.id);
        if (mark) {
          const g = G[ACC_GLYPH[mark.alter]];
          this.add(items, { k: 'glyph', x: x - g.adv * sp * scale - 0.15 * sp, y, ch: g.ch, size: this.fontSize * scale, color: ink, id: n.id });
        }
        ext(y - 3 * sp);
      }
      bb.noteYs = ys;
      if (!ys.length) return;
      const top = Math.min(...ys);
      const sx = x + headW - 0.06 * sp;
      const sy = top - 2.5 * sp;
      this.add(items, { k: 'line', x1: sx, y1: Math.max(...ys) - 0.1 * sp, x2: sx, y2: sy, w: 0.1 * sp, color: ink, id: b.id });
      this.add(items, { k: 'glyph', x: sx - 0.05 * sp, y: sy, ch: G.flag8thUp.ch, size: this.fontSize * scale, color: ink, id: b.id });
      if (b.grace === 'acciaccatura') {
        this.add(items, { k: 'line', x1: sx - 0.6 * sp, y1: sy + 1.6 * sp, x2: sx + 0.8 * sp, y2: sy + 0.6 * sp, w: 0.1 * sp, color: ink, id: b.id });
      }
      x += 1.5 * sp;
    });
  }

  drawBeamGroup(items: DrawItem[], gs: BeatGeom[], ext: (y: number) => void): void {
    const { sp, ink } = this;
    if (gs.length < 2) return;
    const up = gs[0].stemDir === 1;
    for (const g of gs) {
      g.stemDir = up ? 1 : -1;
      g.stemX = up ? g.x + g.headW - (ENGRAVING.stemThickness * sp) / 2 : g.x + (ENGRAVING.stemThickness * sp) / 2;
    }
    const maxLevel = Math.max(...gs.map((g) => beamCount(g.beat.duration.base)));
    const anchor = (g: BeatGeom) => (up ? Math.min(...g.notes.map((n) => n.y)) : Math.max(...g.notes.map((n) => n.y)));
    const first = gs[0];
    const last = gs[gs.length - 1];
    const dx = last.stemX - first.stemX || 1;
    let rise = anchor(last) - anchor(first);
    rise = Math.max(-1 * sp, Math.min(1 * sp, rise));
    // Concave groups (an inner note more extreme than both ends) get a flat beam.
    const innerExtreme = gs.slice(1, -1).some((g) => (up ? anchor(g) < Math.min(anchor(first), anchor(last)) : anchor(g) > Math.max(anchor(first), anchor(last))));
    if (innerExtreme) rise = 0;
    const slope = rise / dx;
    const minStem = (3.0 + (maxLevel - 1) * 0.75) * sp;
    // Beam line y = b + slope * (x - first.stemX); choose b to satisfy all stems.
    let b = up ? Infinity : -Infinity;
    for (const g of gs) {
      const need = up ? anchor(g) - minStem : anchor(g) + minStem;
      const bi = need - slope * (g.stemX - first.stemX);
      b = up ? Math.min(b, bi) : Math.max(b, bi);
    }
    // Prefer the standard 3.5 sp on the first note when possible.
    const pref = up ? anchor(first) - 3.5 * sp : anchor(first) + 3.5 * sp;
    b = up ? Math.min(b, pref) : Math.max(b, pref);
    // Notes far outside the staff: beam reaches the middle line.
    if (up && b > 2 * sp && gs.every((g) => anchor(g) > 4 * sp)) b = Math.min(b, 2 * sp + 0.0);
    if (!up && b < 2 * sp && gs.every((g) => anchor(g) < 0)) b = Math.max(b, 2 * sp);
    const beamY = (x: number) => b + slope * (x - first.stemX);
    const thick = ENGRAVING.beamThickness * sp;
    const levelGap = (ENGRAVING.beamThickness + ENGRAVING.beamSpacing) * sp;
    for (const g of gs) {
      const y = beamY(g.stemX);
      if (up) {
        g.stemTop = y;
        g.stemBottom = Math.max(...g.notes.map((n) => n.y)) - 0.168 * sp;
      } else {
        g.stemBottom = y;
        g.stemTop = Math.min(...g.notes.map((n) => n.y)) + 0.168 * sp;
      }
    }
    const color = gs[0].beat.color ?? ink;
    const id = gs[0].beat.id;
    const beamPoly = (xa: number, xb: number, level: number) => {
      const off = (up ? 1 : -1) * level * levelGap;
      const ya = beamY(xa) + off;
      const yb = beamY(xb) + off;
      const th = up ? thick : -thick;
      this.add(items, { k: 'poly', pts: [xa, ya, xb, yb, xb, yb + th, xa, ya + th], fill: color, id });
      ext(ya);
      ext(yb + th);
    };
    const halfStem = (ENGRAVING.stemThickness * sp) / 2;
    beamPoly(first.stemX - halfStem, last.stemX + halfStem, 0);
    for (let level = 1; level < maxLevel; level++) {
      // Runs of beats needing this level.
      let k = 0;
      while (k < gs.length) {
        if (beamCount(gs[k].beat.duration.base) <= level) {
          k++;
          continue;
        }
        let e = k;
        while (e + 1 < gs.length && beamCount(gs[e + 1].beat.duration.base) > level) e++;
        if (e > k) beamPoly(gs[k].stemX - halfStem, gs[e].stemX + halfStem, level);
        else {
          // Partial beam (hook): right unless it's the last note.
          const hook = 1.1 * sp;
          if (k === gs.length - 1) beamPoly(gs[k].stemX - hook, gs[k].stemX + halfStem, level);
          else beamPoly(gs[k].stemX - halfStem, gs[k].stemX + hook, level);
        }
        k = e + 1;
      }
    }
  }

  drawArticulations(items: DrawItem[], g: BeatGeom, ext: (y: number) => void): void {
    const { sp, ink } = this;
    const b = g.beat;
    const arts = b.articulations ?? [];
    const cx = g.x + g.headW / 2;
    // Articulations sit on the notehead side (opposite the stem), fermata above the staff.
    const below = g.stemDir === 1;
    let y = below ? Math.max(...g.notes.map((n) => n.y)) + 1.0 * sp : Math.min(...g.notes.map((n) => n.y)) - 1.0 * sp;
    if (below && y < 4.5 * sp && y > 4 * sp - 0.1) y = 4.5 * sp;
    const order: [string, keyof typeof G, keyof typeof G][] = [
      ['staccato', 'articStaccatoAbove', 'articStaccatoBelow'],
      ['staccatissimo', 'articStaccatissimoAbove', 'articStaccatissimoBelow'],
      ['tenuto', 'articTenutoAbove', 'articTenutoBelow'],
      ['accent', 'articAccentAbove', 'articAccentBelow'],
      ['marcato', 'articMarcatoAbove', 'articMarcatoBelow'],
    ];
    for (const [name, above, belowG] of order) {
      if (!arts.includes(name as never)) continue;
      const gl = G[below ? belowG : above];
      // Keep staccato dots in spaces.
      let yy = y;
      if (name === 'staccato' && yy >= 0 && yy <= 4 * sp) {
        const pos = Math.round((2 * sp - yy) / (sp / 2));
        if (pos % 2 === 0) yy += below ? sp / 2 : -sp / 2;
      }
      this.add(items, { k: 'glyph', x: cx - (gl.adv * sp) / 2, y: yy + (below ? 0 : 0), ch: gl.ch, size: this.fontSize, color: ink, id: b.id });
      ext(yy + (below ? 1.2 * sp : -1.2 * sp));
      y += below ? 1.0 * sp : -1.0 * sp;
    }
    if (arts.includes('fermata')) {
      const top = Math.min(-1 * sp, g.stemDir === 1 ? g.stemTop : Math.min(...g.notes.map((n) => n.y))) - 1.2 * sp;
      this.add(items, { k: 'glyph', x: cx - (G.fermataAbove.adv * sp) / 2, y: top, ch: G.fermataAbove.ch, size: this.fontSize, color: ink, id: b.id });
      ext(top - 1.4 * sp);
    }
    if (b.ornament === 'trill') {
      const top = Math.min(-1 * sp, g.stemDir === 1 ? g.stemTop : Math.min(...g.notes.map((n) => n.y))) - 1.2 * sp;
      this.add(items, { k: 'glyph', x: cx - (G.ornamentTrill.adv * sp) / 2, y: top, ch: G.ornamentTrill.ch, size: this.fontSize, color: ink, id: b.id });
      ext(top - 1.6 * sp);
    }
    if (b.jazz) {
      const map = { scoop: 'brassScoop', plop: 'brassPlop', doit: 'brassDoitMedium', fall: 'brassFallLipMedium' } as const;
      const gl = G[map[b.jazz]];
      const n = b.jazz === 'scoop' || b.jazz === 'plop' ? g.notes[0] : g.notes[g.notes.length - 1];
      const before = b.jazz === 'scoop' || b.jazz === 'plop';
      this.add(items, { k: 'glyph', x: before ? g.x - gl.adv * sp - 0.2 * sp : g.x + g.headW + 0.3 * sp, y: n.y, ch: gl.ch, size: this.fontSize, color: ink, id: b.id });
    }
    if (b.mute) {
      const map = { open: 'brassMuteOpen', closed: 'brassMuteClosed', halfOpen1: 'brassMuteHalfClosed', halfOpen2: 'brassMuteHalfClosed' } as const;
      const gl = G[map[b.mute]];
      const top = Math.min(-1 * sp, Math.min(...g.notes.map((n) => n.y)) - 1.5 * sp);
      this.add(items, { k: 'glyph', x: cx - (gl.adv * sp) / 2, y: top, ch: gl.ch, size: this.fontSize, color: ink, id: b.id });
      ext(top - 1.4 * sp);
    }
    if (b.pick || b.strum) {
      const top = Math.min(-1 * sp, Math.min(...g.notes.map((n) => n.y)) - 1.5 * sp, g.stemDir === 1 ? g.stemTop - 0.6 * sp : 0);
      if (b.pick) {
        const gl = b.pick === 'down' ? G.stringsDownBow : G.stringsUpBow;
        this.add(items, { k: 'glyph', x: cx - (gl.adv * sp) / 2, y: top, ch: gl.ch, size: this.fontSize, color: ink, id: b.id });
        ext(top - 2 * sp);
      }
    }
    if (b.breath) {
      this.add(items, { k: 'glyph', x: g.x + g.headW + 1.2 * sp, y: -0.5 * sp, ch: G.breathMarkComma.ch, size: this.fontSize, color: ink, id: b.id });
    }
  }

  drawArpeggio(items: DrawItem[], g: BeatGeom, accW: number): void {
    const { sp, ink } = this;
    const b = g.beat;
    const kind = b.arpeggio ?? b.strum ?? b.brush;
    if (!kind || g.notes.length === 0) return;
    const top = Math.min(...g.notes.map((n) => n.y)) - 0.8 * sp;
    const bottom = Math.max(...g.notes.map((n) => n.y)) + 0.8 * sp;
    const x = g.x - accW - 1.1 * sp;
    // Wavy line drawn as a path; arrow head shows direction.
    const pts: number[] = [];
    for (let y = bottom, k = 0; y >= top; y -= sp / 2, k++) pts.push(x + (k % 2 ? 0.35 * sp : -0.35 * sp) * (b.brush ? 0 : 1), y);
    this.add(items, { k: 'path', pts, w: 0.14 * sp, color: ink, id: b.id });
    const upArrow = kind === 'up';
    const ay = upArrow ? top : bottom;
    const d = upArrow ? -1 : 1;
    this.add(items, { k: 'poly', pts: [x - 0.45 * sp, ay - d * 0.7 * sp, x + 0.45 * sp, ay - d * 0.7 * sp, x, ay + d * 0.2 * sp], fill: ink, id: b.id });
  }

  drawTiesAndSlurs(items: DrawItem[], sys: SystemBox, g: BeatGeom, t: number, bbx: BarBox): void {
    const { sp, ink } = this;
    const b = g.beat;
    const vkey = `${t}:${g.vd.voice}`;
    const systemStartX = bbx.contentX - 0.5 * sp;
    // Resolve incoming ties.
    for (const ng of g.notes) {
      const key = `${vkey}:${midiOf(ng.note.pitch)}`;
      const p = this.pendingTies.get(key);
      if (!p) continue;
      this.pendingTies.delete(key);
      const endX = ng.x - 0.15 * sp;
      if (p.system === sys.index) this.tieCurve(items, p.x, endX, ng.y, p.up, ng.note.id);
      else this.tieCurve(items, systemStartX - 1.5 * sp, endX, ng.y, p.up, ng.note.id);
    }
    // Drop unresolved ties for this voice (they were not continued).
    for (const [k, p] of this.pendingTies) {
      if (k.startsWith(vkey + ':') && p.id !== b.id) {
        this.pendingTies.delete(k);
      }
    }
    // Outgoing ties.
    const sortedByY = [...g.notes].sort((a, c) => a.y - c.y);
    sortedByY.forEach((ng, k) => {
      if (!ng.note.tieToNext) return;
      const up = g.notes.length > 1 ? k < g.notes.length / 2 : g.stemDir === -1;
      const key = `${vkey}:${midiOf(ng.note.pitch)}`;
      const startX = ng.x + g.headW + 0.15 * sp;
      this.pendingTies.set(key, { x: startX, y: ng.y, up, system: sys.index, id: b.id });
      // If the continuation is in a later system, draw the first half now to the system end.
      const nextInSystem = this.nextBeatInSystem(sys, g, t);
      if (!nextInSystem) this.tieCurve(items, startX, sys.x1 - 0.3 * sp, ng.y, up, ng.note.id);
    });
    // Slurs.
    const topNote = g.notes.reduce((a, c) => (c.y < a.y ? c : a), g.notes[0]);
    const botNote = g.notes.reduce((a, c) => (c.y > a.y ? c : a), g.notes[0]);
    for (const ng of g.notes) {
      if (ng.note.slurEnd) {
        const p = this.pendingSlurs.get(vkey);
        if (p) {
          this.pendingSlurs.delete(vkey);
          const ref = p.above ? topNote : botNote;
          const endY = ref.y + (p.above ? -0.9 * sp : 0.9 * sp);
          if (p.system === sys.index) this.slurCurve(items, p.x, p.y, ng.x + g.headW / 2, endY, p.above, p.id);
          else this.slurCurve(items, systemStartX - 1 * sp, endY, ng.x + g.headW / 2, endY, p.above, p.id);
        }
      }
    }
    for (const ng of g.notes) {
      if (ng.note.slurStart) {
        const side = ng.note.slurSide;
        const above = side === 'above' ? true : side === 'below' ? false : g.stemDir === -1;
        const ref = above ? topNote : botNote;
        const y = ref.y + (above ? -0.9 * sp : 0.9 * sp);
        this.pendingSlurs.set(vkey, { x: g.x + g.headW / 2, y, above, system: sys.index, id: ng.note.id });
        break;
      }
    }
    void ink;
  }

  nextBeatInSystem(sys: SystemBox, g: BeatGeom, t: number): boolean {
    const vd = g.vd;
    if (g.idx + 1 < vd.beats.length) return true;
    const barPos = sys.bars.indexOf(this.bars.find((bd) => bd.voices.includes(vd))?.bar ?? -1);
    if (barPos < 0) return false;
    if (barPos + 1 < sys.bars.length) return true;
    void t;
    return false;
  }

  tieCurve(items: DrawItem[], x1: number, x2: number, y: number, up: boolean, id: string): void {
    const { sp } = this;
    const d = up ? -1 : 1;
    const y0 = y + d * 0.55 * sp;
    const h = Math.min(1.1 * sp, 0.4 * sp + (x2 - x1) * 0.06);
    this.add(items, { k: 'curve', x1, y1: y0, cx1: x1 + (x2 - x1) * 0.25, cy1: y0 + d * h, cx2: x1 + (x2 - x1) * 0.75, cy2: y0 + d * h, x2, y2: y0, thick: ENGRAVING.tieMidpointThickness * sp, color: this.ink, id });
  }

  slurCurve(items: DrawItem[], x1: number, y1: number, x2: number, y2: number, above: boolean, id: string): void {
    const { sp } = this;
    const d = above ? -1 : 1;
    const h = Math.min(2.5 * sp, 0.8 * sp + Math.abs(x2 - x1) * 0.08);
    const yc = (above ? Math.min(y1, y2) : Math.max(y1, y2)) + d * h;
    this.add(items, { k: 'curve', x1, y1, cx1: x1 + (x2 - x1) * 0.2, cy1: yc, cx2: x1 + (x2 - x1) * 0.8, cy2: yc, x2, y2, thick: ENGRAVING.slurMidpointThickness * sp, color: this.ink, id });
  }

  // ------------------------------------------------------------ rows above the top band
  /** `timeX`: left edge of the time signature in the system header, when one is drawn. */
  aboveRows(items: DrawItem[], sys: SystemBox, barBoxes: BarBox[], t: number, rowTop: number, topTrack: boolean, ext: (y: number) => void, timeX?: number): number {
    const { sp, ink } = this;
    const track = this.score.tracks[t];
    let y = rowTop;
    const beatsOf = (bbx: BarBox) => this.bars[bbx.bar].voices.filter((v) => v.track === t);
    const xOfBeat = (bbx: BarBox, vd: VoiceData, i: number) => bbx.columns.find((c) => c.tick === vd.onsets[i])?.x ?? bbx.contentX;

    // Palm mute / let ring (only when there is no tab band; otherwise drawn above the tab).
    if (!track.showTab) y = this.techniqueLines(items, barBoxes, t, y, ext);

    // Octave lines.
    let hasOctave = false;
    for (const bbx of barBoxes) for (const vd of beatsOf(bbx)) if (vd.beats.some((b) => b.octaveShift)) hasOctave = true;
    if (hasOctave) {
      y -= 1.4 * sp;
      for (const bbx of barBoxes) {
        for (const vd of beatsOf(bbx)) {
          vd.beats.forEach((b, i) => {
            if (!b.octaveShift) return;
            const x = xOfBeat(bbx, vd, i);
            const label = b.octaveShift === 8 ? '8va' : b.octaveShift === -8 ? '8vb' : b.octaveShift === 15 ? '15ma' : '15mb';
            this.add(items, { k: 'text', x, y, text: label, font: `italic 700 ${(1.2 * sp).toFixed(1)}px Georgia, serif`, color: ink, baseline: 'middle', id: b.id });
            this.add(items, { k: 'line', x1: x + 2.2 * sp, y1: y, x2: x + 4 * sp, y2: y, w: 0.12 * sp, color: ink, dash: [0.4 * sp, 0.3 * sp], id: b.id });
          });
        }
      }
      ext(y - sp);
    }

    // Inner text above.
    const innerAbove = this.collectBeatText(barBoxes, t, (b) => b.text?.innerAbove);
    if (innerAbove.length) {
      y -= 1.6 * sp;
      for (const it of innerAbove) this.add(items, { k: 'text', x: it.x, y, text: it.text, font: this.textFont, color: ink, baseline: 'alphabetic', id: it.id });
      ext(y - 1.5 * sp);
    }

    // Chord names (and diagrams as compact frets text).
    const chords = this.collectBeatText(barBoxes, t, (b) => b.chord?.name);
    if (chords.length) {
      y -= 2.2 * sp;
      for (const it of chords) this.add(items, { k: 'text', x: it.x, y, text: it.text, font: this.chordFont, color: ink, baseline: 'alphabetic', id: it.id });
      ext(y - 1.6 * sp);
    }

    // Text above.
    const above = this.collectBeatText(barBoxes, t, (b) => b.text?.above);
    if (above.length) {
      y -= 2.0 * sp;
      for (const it of above) this.add(items, { k: 'text', x: it.x, y, text: it.text, font: this.textFont, color: ink, baseline: 'alphabetic', id: it.id });
      ext(y - 1.6 * sp);
    }

    // Lyrics on a tab-less, staff-only track live below; nothing to do here.
    if (!topTrack) return y;

    // Voltas, sections, tempo, directions, repeat counts, bar numbers.
    const hasVolta = barBoxes.some((bx) => this.bars[bx.bar].info.endings?.length);
    if (hasVolta) {
      y -= 2.2 * sp;
      let k = 0;
      while (k < barBoxes.length) {
        const e = this.bars[barBoxes[k].bar].info.endings;
        if (!e?.length) {
          k++;
          continue;
        }
        let end = k;
        while (end + 1 < barBoxes.length && sameArr(this.bars[barBoxes[end + 1].bar].info.endings, e)) end++;
        const xa = barBoxes[k].x0 + 0.2 * sp;
        const xb = barBoxes[end].x1 - 0.6 * sp;
        const continues = end === barBoxes.length - 1 && barBoxes[end].bar + 1 < this.score.bars.length && sameArr(this.score.bars[barBoxes[end].bar + 1].endings, e);
        const startsHere = !(k === 0 && barBoxes[0].bar > 0 && sameArr(this.score.bars[barBoxes[0].bar - 1].endings, e));
        const closed = !continues && !!this.score.bars[barBoxes[end].bar].repeatEnd;
        const pts = [xa, y + (startsHere ? 2 * sp : 0), xa, y, xb, y];
        if (closed) pts.push(xb, y + 2 * sp);
        this.add(items, { k: 'path', pts, w: 0.16 * sp, color: ink });
        if (startsHere) this.add(items, { k: 'text', x: xa + 0.5 * sp, y: y + 1.5 * sp, text: e.join(', ') + '.', font: `${(1.3 * sp).toFixed(1)}px Georgia, serif`, color: ink });
        k = end + 1;
      }
      ext(y - 0.5 * sp);
    }

    let rowY = y - 2.2 * sp;
    let used = false;
    for (const bbx of barBoxes) {
      const info = this.bars[bbx.bar].info;
      let x = bbx.x0 + 0.2 * sp;
      // A tempo mark opening the system sits over the time signature, as in
      // engraved music; after a section label it follows the label instead.
      let overTime = bbx === barBoxes[0] && timeX !== undefined && !info.section?.letter && !info.section?.name;
      if (info.section?.letter) {
        const w = Math.max(2.2 * sp, this.measure(info.section.letter, `700 ${(1.5 * sp).toFixed(1)}px Inter, Arial, sans-serif`) + 1 * sp);
        this.add(items, { k: 'rect', x, y: rowY - 1.7 * sp, w, h: 2.3 * sp, stroke: ink, lw: 0.14 * sp });
        this.add(items, { k: 'text', x: x + w / 2, y: rowY, text: info.section.letter, font: `700 ${(1.5 * sp).toFixed(1)}px Inter, Arial, sans-serif`, color: ink, align: 'center' });
        x += w + 0.6 * sp;
        used = true;
      }
      if (info.section?.name) {
        this.add(items, { k: 'text', x, y: rowY, text: info.section.name, font: `700 ${(1.45 * sp).toFixed(1)}px Inter, Arial, sans-serif`, color: ink });
        x += this.measure(info.section.name, `700 ${(1.45 * sp).toFixed(1)}px Inter, Arial, sans-serif`) + 1 * sp;
        used = true;
      }
      for (const tm of this.score.tempos.filter((m) => m.bar === bbx.bar)) {
        const tx = tm.beatOffsetTicks ? bbx.contentX + 1.3 * sp + (tm.beatOffsetTicks / bbx.length) * (bbx.x1 - bbx.contentX) : overTime && timeX !== undefined ? timeX : x;
        overTime = false;
        x = this.drawTempo(items, tm, tx, rowY) + 1 * sp;
        used = true;
      }
      if (info.direction) {
        const d = info.direction;
        if (d === 'Segno' || d === 'Coda') {
          const g = d === 'Segno' ? G.segno : G.coda;
          this.add(items, { k: 'glyph', x, y: rowY + 0.3 * sp, ch: g.ch, size: this.fontSize * 0.85, color: ink });
        } else {
          const label = d === 'DC' ? 'D.C.' : d === 'DS' ? 'D.S.' : d === 'ToCoda' ? 'To Coda' : d.replace('DC', 'D.C.').replace('DS', 'D.S.');
          this.add(items, { k: 'text', x: bbx.x1 - 0.5 * sp, y: rowY, text: label, font: `italic 700 ${(1.4 * sp).toFixed(1)}px Georgia, serif`, color: ink, align: 'right' });
          if (d === 'ToCoda') {
            this.add(items, { k: 'glyph', x: bbx.x1 + 0.2 * sp, y: rowY + 0.3 * sp, ch: G.coda.ch, size: this.fontSize * 0.7, color: ink });
          }
        }
        used = true;
      }
      if (info.repeatEnd && info.repeatEnd > 2) {
        this.add(items, { k: 'text', x: bbx.x1 - 0.5 * sp, y: rowY, text: `×${info.repeatEnd}`, font: `700 ${(1.3 * sp).toFixed(1)}px Inter, Arial, sans-serif`, color: ink, align: 'right' });
        used = true;
      }
      if (info.commentary) {
        this.add(items, { k: 'text', x: bbx.contentX, y: rowY, text: '💬 ' + info.commentary.slice(0, 40), font: `${(1.1 * sp).toFixed(1)}px Inter, Arial, sans-serif`, color: '#7a6a5a' });
        used = true;
      }
    }
    if (used) {
      y = rowY - 1.8 * sp;
      ext(y);
    }
    return y;
  }

  drawTempo(items: DrawItem[], tm: { bpm: number; beatUnit: number; dotted?: boolean; text?: string }, x: number, y: number): number {
    const { sp, ink } = this;
    let cx = x;
    const font = `700 ${(1.4 * sp).toFixed(1)}px Georgia, serif`;
    if (tm.text) {
      this.add(items, { k: 'text', x: cx, y, text: tm.text, font, color: ink });
      cx += this.measure(tm.text, font) + 0.8 * sp;
    }
    const g = tm.beatUnit === 2 ? G.metNoteHalfUp : tm.beatUnit === 8 ? G.metNote8thUp : G.metNoteQuarterUp;
    this.add(items, { k: 'glyph', x: cx, y, ch: g.ch, size: this.fontSize * 0.75, color: ink });
    cx += g.adv * sp * 0.75 + 0.2 * sp;
    if (tm.dotted) {
      this.add(items, { k: 'glyph', x: cx, y: y - 0.1 * sp, ch: G.metAugmentationDot.ch, size: this.fontSize * 0.75, color: ink });
      cx += 0.5 * sp;
    }
    const txt = ` = ${tm.bpm}`;
    const f2 = `${(1.4 * sp).toFixed(1)}px Georgia, serif`;
    this.add(items, { k: 'text', x: cx, y, text: txt, font: f2, color: ink });
    return cx + this.measure(txt, f2);
  }

  collectBeatText(barBoxes: BarBox[], t: number, get: (b: Beat) => string | undefined): { x: number; text: string; id: string }[] {
    const out: { x: number; text: string; id: string }[] = [];
    for (const bbx of barBoxes) {
      for (const vd of this.bars[bbx.bar].voices) {
        if (vd.track !== t) continue;
        vd.beats.forEach((b, i) => {
          const text = get(b);
          if (!text) return;
          const x = bbx.columns.find((c) => c.tick === vd.onsets[i])?.x ?? bbx.contentX;
          out.push({ x, text, id: b.id });
        });
      }
    }
    return out;
  }

  /** P.M. / let ring / vibrato lines above a band. Returns the new top. */
  techniqueLines(items: DrawItem[], barBoxes: BarBox[], t: number, y: number, ext: (y: number) => void): number {
    const { sp, ink } = this;
    const rows: { key: 'palmMute' | 'letRing'; label: string }[] = [
      { key: 'palmMute', label: 'P.M.' },
      { key: 'letRing', label: 'let ring' },
    ];
    for (const row of rows) {
      const runs: { xa: number; xb: number; id: string }[] = [];
      let cur: { xa: number; xb: number; id: string } | null = null;
      for (const bbx of barBoxes) {
        const vd = this.bars[bbx.bar].voices.find((v) => v.track === t && v.voice === 0);
        if (!vd) continue;
        vd.beats.forEach((b, i) => {
          const x = bbx.columns.find((c) => c.tick === vd.onsets[i])?.x ?? bbx.contentX;
          const xe = (() => {
            const ci = bbx.columns.findIndex((c) => c.tick === vd.onsets[i]);
            return ci >= 0 && ci + 1 < bbx.columns.length ? bbx.columns[ci + 1].x : bbx.x1;
          })();
          if (b[row.key]) {
            if (!cur) cur = { xa: x, xb: xe - 0.6 * sp, id: b.id };
            else cur.xb = xe - 0.6 * sp;
          } else if (cur) {
            runs.push(cur);
            cur = null;
          }
        });
      }
      if (cur) runs.push(cur);
      if (!runs.length) continue;
      y -= 1.6 * sp;
      const font = `italic ${(1.15 * sp).toFixed(1)}px Georgia, serif`;
      for (const r of runs) {
        this.add(items, { k: 'text', x: r.xa, y, text: row.label, font, color: ink, baseline: 'middle', id: r.id });
        const lx = r.xa + this.measure(row.label, font) + 0.4 * sp;
        if (r.xb > lx) {
          this.add(items, { k: 'line', x1: lx, y1: y, x2: r.xb, y2: y, w: 0.1 * sp, color: ink, dash: [0.5 * sp, 0.35 * sp] });
          this.add(items, { k: 'line', x1: r.xb, y1: y - 0.4 * sp, x2: r.xb, y2: y + 0.4 * sp, w: 0.1 * sp, color: ink });
        }
      }
      ext(y - sp);
    }
    // Vibrato wiggles.
    let any = false;
    for (const bbx of barBoxes) {
      for (const vd of this.bars[bbx.bar].voices) {
        if (vd.track !== t) continue;
        vd.beats.forEach((b, i) => {
          if (!b.notes.some((n) => n.vibrato)) return;
          if (!any) {
            y -= 1.3 * sp;
            any = true;
          }
          const x = bbx.columns.find((c) => c.tick === vd.onsets[i])?.x ?? bbx.contentX;
          const pts: number[] = [];
          for (let k = 0; k <= 8; k++) pts.push(x + k * 0.4 * sp, y + (k % 2 ? -0.3 * sp : 0.3 * sp));
          this.add(items, { k: 'path', pts, w: 0.14 * sp, color: ink, id: b.id });
        });
      }
    }
    if (any) ext(y - sp);
    return y;
  }

  // ------------------------------------------------------------ rows below the staff
  belowRows(items: DrawItem[], sys: SystemBox, barBoxes: BarBox[], t: number, rowBottom: number, isBottom: boolean, ext: (y: number) => void): number {
    const { sp, ink } = this;
    let y = rowBottom;
    // Dynamics + hairpins.
    const dyns = this.collectBeatText(barBoxes, t, (b) => b.dynamics);
    const hairpins: { xa: number; xb: number; type: 'cresc' | 'dim'; id: string }[] = [];
    for (const bbx of barBoxes) {
      for (const vd of this.bars[bbx.bar].voices) {
        if (vd.track !== t) continue;
        vd.beats.forEach((b, i) => {
          if (!b.hairpin) return;
          const xa = bbx.columns.find((c) => c.tick === vd.onsets[i])?.x ?? bbx.contentX;
          const end = this.findBeatX(sys, barBoxes, t, b.hairpin.endBeatId);
          hairpins.push({ xa, xb: end ?? sys.x1 - sp, type: b.hairpin.type, id: b.id });
        });
      }
    }
    if (dyns.length || hairpins.length) {
      y += 0.5 * sp;
      for (const d of dyns) {
        const g = G[DYN_GLYPH[d.text]];
        if (g) this.add(items, { k: 'glyph', x: d.x - 0.2 * sp, y: y + 0.5 * sp, ch: g.ch, size: this.fontSize, color: ink, id: d.id });
      }
      for (const h of hairpins) {
        const xa = h.xa + (dyns.some((d) => Math.abs(d.x - h.xa) < sp) ? 2.6 * sp : 0);
        const xb = Math.max(xa + 2 * sp, h.xb - 0.4 * sp);
        const open = 0.6 * sp;
        if (h.type === 'cresc') this.add(items, { k: 'path', pts: [xb, y - open, xa, y, xb, y + open], w: 0.12 * sp, color: ink, id: h.id });
        else this.add(items, { k: 'path', pts: [xa, y - open, xb, y, xa, y + open], w: 0.12 * sp, color: ink, id: h.id });
      }
      ext(y + 1.6 * sp);
      y += 2.0 * sp;
    }
    const innerBelow = this.collectBeatText(barBoxes, t, (b) => b.text?.innerBelow);
    if (innerBelow.length) {
      y += 0.8 * sp;
      for (const it of innerBelow) this.add(items, { k: 'text', x: it.x, y, text: it.text, font: this.textFont, color: ink, id: it.id });
      ext(y + 0.6 * sp);
      y += 1 * sp;
    }
    // Pedal marks.
    const peds = this.collectBeatText(barBoxes, t, (b) => b.pedal);
    if (peds.length) {
      y += 1.2 * sp;
      for (const p of peds) {
        const g = p.text === 'down' ? G.keyboardPedalPed : G.keyboardPedalUp;
        this.add(items, { k: 'glyph', x: p.x, y, ch: g.ch, size: this.fontSize * 0.8, color: ink, id: p.id });
      }
      ext(y + 0.6 * sp);
      y += 0.8 * sp;
    }
    if (isBottom) y = this.bottomText(items, barBoxes, t, y, ext);
    return y;
  }

  /** Text below, sticking, Roman numerals and lyrics: drawn under the last band of a track. */
  bottomText(items: DrawItem[], barBoxes: BarBox[], t: number, y: number, ext: (y: number) => void): number {
    const { sp, ink } = this;
    const rows: [(b: Beat) => string | undefined, string][] = [
      [(b) => b.text?.below, this.textFont],
      [(b) => b.roman, `${(1.4 * sp).toFixed(1)}px Georgia, serif`],
      [(b) => b.sticking, `700 ${(1.2 * sp).toFixed(1)}px Inter, Arial, sans-serif`],
    ];
    for (const [get, font] of rows) {
      const list = this.collectBeatText(barBoxes, t, get);
      if (!list.length) continue;
      y += 1.6 * sp;
      for (const it of list) this.add(items, { k: 'text', x: it.x, y, text: it.text, font, color: ink, id: it.id });
      ext(y + 0.6 * sp);
    }
    // Lyrics: one row per verse.
    let verses = 0;
    for (const bbx of barBoxes) for (const vd of this.bars[bbx.bar].voices) if (vd.track === t) for (const b of vd.beats) verses = Math.max(verses, b.lyrics?.length ?? 0);
    for (let v = 0; v < verses; v++) {
      y += 1.9 * sp;
      const list = this.collectBeatText(barBoxes, t, (b) => b.lyrics?.[v]);
      for (const it of list) {
        const hyphen = it.text.endsWith('-');
        const text = hyphen ? it.text.slice(0, -1) : it.text;
        this.add(items, { k: 'text', x: it.x - 0.2 * sp, y, text, font: this.lyricFont, color: ink, id: it.id });
        if (hyphen) {
          const w = this.measure(text, this.lyricFont);
          this.add(items, { k: 'text', x: it.x + w + 0.8 * sp, y, text: '-', font: this.lyricFont, color: ink });
        }
      }
      ext(y + 0.6 * sp);
    }
    return y;
  }

  findBeatX(sys: SystemBox, barBoxes: BarBox[], t: number, beatId: string): number | null {
    for (const bbx of barBoxes) {
      for (const vd of this.bars[bbx.bar].voices) {
        if (vd.track !== t) continue;
        const i = vd.beats.findIndex((b) => b.id === beatId);
        if (i >= 0) return bbx.columns.find((c) => c.tick === vd.onsets[i])?.x ?? null;
      }
    }
    void sys;
    return null;
  }

  // ------------------------------------------------------------ tab band
  tabBand(sys: SystemBox, barBoxes: BarBox[], t: number, isTop: boolean, isBottom: boolean, topTrack: boolean) {
    const { sp } = this;
    const items: DrawItem[] = [];
    const track = this.score.tracks[t];
    const strings = track.tuning!.length;
    const gap = TAB_GAP * sp;
    const height = (strings - 1) * gap;
    const ink = this.tabInk;
    let minY = 0;
    let maxY = height;
    const ext = (y: number) => {
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    };
    const beatBoxes: BeatBox[] = [];
    const lx0 = this.staffStart(sys);
    for (let s = 0; s < strings; s++) {
      this.add(items, { k: 'line', x1: lx0, y1: s * gap, x2: sys.x1, y2: s * gap, w: ENGRAVING.staffLineThickness * sp, color: '#666' });
    }
    // TAB clef.
    const hx = sys.x0 + this.instrumentLabelWidth(sys.index === 0) + (this.tracks.length > 1 ? 1.2 * sp : 0) + 1.2 * sp;
    const letters = ['T', 'A', 'B'];
    const clefFont = `700 ${(Math.min(1.9, height / (3.4 * sp)) * sp).toFixed(1)}px Georgia, 'Times New Roman', serif`;
    letters.forEach((l, i) => {
      this.add(items, { k: 'text', x: hx, y: height / 2 + (i - 1) * Math.min(1.75 * sp, height / 3.2), text: l, font: clefFont, color: ink, baseline: 'middle', align: 'left' });
    });
    if (!track.showStaff && sys.index === 0) {
      this.drawTime(items, this.bars[barBoxes[0].bar].ts, hx + 2.8 * sp, height);
    }
    if (isTop && !track.showStaff) {
      // Tempo/sections/chords when the tab is the top band.
      const top = this.aboveRows(items, sys, barBoxes, t, -1.4 * sp, topTrack, ext);
      minY = Math.min(minY, top);
    }
    const tabStems = track.tabStems ?? !track.showStaff;
    for (const bbx of barBoxes) {
      const bd = this.bars[bbx.bar];
      if (bd.multiRest) {
        this.drawMultiRestTab(items, bbx, bd.multiRest, height);
        continue;
      }
      for (const vd of bd.voices) {
        if (vd.track !== t) continue;
        const colX = (tick: number) => bbx.columns.find((c) => c.tick === tick)?.x ?? bbx.contentX;
        const colEnd = (tick: number) => {
          const i = bbx.columns.findIndex((c) => c.tick === tick);
          return i >= 0 && i + 1 < bbx.columns.length ? bbx.columns[i + 1].x : bbx.x1;
        };
        const headW = G.noteheadBlack.adv * sp;
        const stemInfo: { x: number; y0: number; b: Beat }[] = [];
        vd.beats.forEach((b, i) => {
          const x = colX(vd.onsets[i]);
          const cx = b.grace ? x - 1.2 * sp : x + headW / 2;
          const bb = this.beatBox(sys, { track: t, bar: bbx.bar, voice: vd.voice, beat: i }, b, x, headW, colEnd(vd.onsets[i]), vd.onsets[i]);
          if (!beatBoxes.includes(bb)) beatBoxes.push(bb);
          if (b.rest) {
            if (tabStems && !b.hidden && vd.voice === 0) {
              const g = G[REST_GLYPH[b.duration.base] ?? 'restQuarter'];
              const scale = 0.7;
              this.add(items, { k: 'glyph', x: cx - (g.adv * sp * scale) / 2, y: height / 2, ch: g.ch, size: this.fontSize * scale, color: '#666', id: b.id });
            }
            bb.tabYs = [];
            return;
          }
          const ys: number[] = [];
          let lowest = -Infinity;
          for (const n of b.notes) {
            if (n.string === undefined || n.string > strings) {
              ys.push(NaN);
              continue;
            }
            const y = (n.string - 1) * gap;
            ys.push(y);
            lowest = Math.max(lowest, y);
            const tiedFrom = this.isTiedFromPrev(t, vd.voice, bbx.bar, i, n);
            let text = this.fretText(n);
            if (tiedFrom && !n.dead) text = `(${n.fret})`;
            const size = b.grace ? 0.75 : 1;
            const font = b.grace ? `${(1.0 * sp).toFixed(1)}px Arial, Helvetica, sans-serif` : this.fretFont;
            const color = n.color && this.opts.noteColors !== 'hide' ? n.color : ink;
            this.add(items, { k: 'text', x: cx, y: y + 0.05 * sp, text, font, color, align: 'center', baseline: 'middle', bg: '#ffffff', bgPad: 0.15 * sp * size, id: n.id });
            // Effects drawn on the tab.
            this.tabEffects(items, sys, n, b, cx, y, t, vd.voice, ext, text);
          }
          bb.tabYs = ys;
          if (tabStems && b.duration.base >= 2 && Number.isFinite(lowest)) stemInfo.push({ x: cx, y0: lowest + 0.9 * sp, b });
        });
        // Rhythm stems below the tab.
        if (tabStems && vd.voice === 0) {
          const stemEnd = height + 2.6 * sp;
          for (const s of stemInfo) {
            this.add(items, { k: 'line', x1: s.x, y1: s.y0, x2: s.x, y2: s.b.duration.base >= 4 ? stemEnd : stemEnd - 0.8 * sp, w: 0.12 * sp, color: ink, id: s.b.id });
            if (s.b.duration.dots) this.add(items, { k: 'glyph', x: s.x + 0.4 * sp, y: stemEnd - 0.8 * sp, ch: G.augmentationDot.ch, size: this.fontSize, color: ink, id: s.b.id });
          }
          const groups = beamGroups(vd.beats, vd.onsets, bd.ts);
          const xOfBeat = (i: number) => colX(vd.onsets[i]) + headW / 2;
          for (const g of groups) {
            if (g.length < 2) {
              const b = vd.beats[g[0]];
              if (beamable(b)) {
                const lvl = beamCount(b.duration.base);
                for (let l = 0; l < lvl; l++) {
                  const yy = stemEnd - l * 0.6 * sp;
                  this.add(items, { k: 'line', x1: xOfBeat(g[0]), y1: yy, x2: xOfBeat(g[0]) + 0.9 * sp, y2: yy - 0.5 * sp, w: 0.2 * sp, color: ink, id: b.id });
                }
              }
              continue;
            }
            const maxL = Math.max(...g.map((i) => beamCount(vd.beats[i].duration.base)));
            for (let l = 0; l < maxL; l++) {
              const yy = stemEnd - l * 0.6 * sp - 0.15 * sp;
              let k = 0;
              while (k < g.length) {
                if (beamCount(vd.beats[g[k]].duration.base) <= l) {
                  k++;
                  continue;
                }
                let e = k;
                while (e + 1 < g.length && beamCount(vd.beats[g[e + 1]].duration.base) > l) e++;
                const xa = xOfBeat(g[k]);
                const xb = e > k ? xOfBeat(g[e]) : xa + (k === g.length - 1 ? -0.9 * sp : 0.9 * sp);
                this.add(items, { k: 'rect', x: Math.min(xa, xb) - 0.06 * sp, y: yy - 0.15 * sp, w: Math.abs(xb - xa) + 0.12 * sp, h: 0.32 * sp, fill: ink, id: vd.beats[g[0]].id });
                k = e + 1;
              }
            }
          }
          ext(stemEnd + 0.5 * sp);
          // Tuplet numbers under the stems.
          for (const tg of tupletGroups(vd.beats)) {
            const xs = tg.map((i) => xOfBeat(i));
            const label = String(vd.beats[tg[0]].duration.tuplet!.actual);
            this.add(items, { k: 'text', x: (Math.min(...xs) + Math.max(...xs)) / 2, y: stemEnd + 1.3 * sp, text: label, font: `italic ${(1.1 * sp).toFixed(1)}px Georgia, serif`, color: ink, align: 'center', baseline: 'middle' });
            ext(stemEnd + 2 * sp);
          }
        }
      }
    }
    // P.M./let ring/vibrato above the tab.
    if (track.showStaff || !isTop) {
      const top = this.techniqueLines(items, barBoxes, t, Math.min(-0.6 * sp, minY), ext);
      minY = Math.min(minY, top);
    } else {
      const top = this.techniqueLines(items, barBoxes, t, minY, ext);
      minY = Math.min(minY, top);
    }
    if (!track.showStaff) this.belowRows(items, sys, barBoxes, t, maxY + 1.2 * sp, false, ext);
    if (isBottom) this.bottomText(items, barBoxes, t, maxY + 0.4 * sp, ext);
    return { items, minY, maxY, beatBoxes };
  }

  drawMultiRestTab(items: DrawItem[], bbx: BarBox, count: number, height: number): void {
    const { sp } = this;
    this.add(items, { k: 'text', x: (bbx.x0 + bbx.x1) / 2, y: height / 2, text: String(count), font: `700 ${(1.6 * sp).toFixed(1)}px Arial, sans-serif`, color: this.tabInk, align: 'center', baseline: 'middle', bg: '#fff', bgPad: 0.3 * sp });
  }

  tabEffects(items: DrawItem[], sys: SystemBox, n: Note, b: Beat, cx: number, y: number, t: number, voice: number, ext: (y: number) => void, text: string): void {
    const { sp } = this;
    const ink = this.tabInk;
    const key = `${t}:${voice}:${n.string}`;
    const textW = this.measure(text, this.fretFont);
    // Resolve a pending hammer-on/pull-off or slide on this string.
    const p = this.pendingLegato.get(key);
    if (p) {
      this.pendingLegato.delete(key);
      if (p.system === sys.index) {
        const midi = midiOf(n.pitch);
        if (p.kind === 'h') {
          const xa = p.x;
          const xb = cx;
          const top = Math.min(p.y, y) - 0.9 * sp;
          this.add(items, { k: 'curve', x1: xa + 0.2 * sp, y1: top + 0.4 * sp, cx1: xa + (xb - xa) * 0.3, cy1: top - 0.5 * sp, cx2: xa + (xb - xa) * 0.7, cy2: top - 0.5 * sp, x2: xb - 0.2 * sp, y2: top + 0.4 * sp, thick: 0.18 * sp, color: ink, id: p.id });
          const label = midi > p.midi ? 'H' : midi < p.midi ? 'P' : '';
          if (label) this.add(items, { k: 'text', x: (xa + xb) / 2, y: top - 0.6 * sp, text: label, font: `${(1.0 * sp).toFixed(1)}px Arial, sans-serif`, color: ink, align: 'center', baseline: 'middle', id: p.id });
          ext(top - 1.2 * sp);
        } else {
          const up = midi > p.midi;
          const xa = p.x + textW / 2 + 0.3 * sp;
          const xb = cx - textW / 2 - 0.3 * sp;
          this.add(items, { k: 'line', x1: xa, y1: y + (up ? 0.45 : -0.45) * sp, x2: xb, y2: y + (up ? -0.45 : 0.45) * sp, w: 0.14 * sp, color: ink, id: p.id });
        }
      }
    }
    if (n.hammerPull) this.pendingLegato.set(key, { x: cx, y, midi: midiOf(n.pitch), kind: 'h', system: sys.index, id: n.id });
    if (n.slide === 'legato' || n.slide === 'shift') this.pendingLegato.set(key, { x: cx, y, midi: midiOf(n.pitch), kind: 'slide', system: sys.index, id: n.id });
    if (n.slide === 'inBelow' || n.slide === 'inAbove') {
      const d = n.slide === 'inBelow' ? 1 : -1;
      this.add(items, { k: 'line', x1: cx - textW / 2 - 1.4 * sp, y1: y + d * 0.45 * sp, x2: cx - textW / 2 - 0.3 * sp, y2: y - d * 0.45 * sp, w: 0.14 * sp, color: ink, id: n.id });
    }
    if (n.slide === 'outDown' || n.slide === 'outUp') {
      const d = n.slide === 'outDown' ? 1 : -1;
      this.add(items, { k: 'line', x1: cx + textW / 2 + 0.3 * sp, y1: y - d * 0.45 * sp, x2: cx + textW / 2 + 1.4 * sp, y2: y + d * 0.45 * sp, w: 0.14 * sp, color: ink, id: n.id });
    }
    if (n.bend?.points.length) {
      const maxSemis = Math.max(...n.bend.points.map((q) => q.semis));
      const label = maxSemis === 2 ? 'full' : maxSemis === 1 ? '½' : maxSemis === 3 ? '1½' : maxSemis === 4 ? '2' : String(maxSemis / 2);
      const x0 = cx + textW / 2 + 0.2 * sp;
      const topY = -2.2 * sp;
      const release = n.bend.points[n.bend.points.length - 1].semis < maxSemis;
      const xPeak = x0 + 1.2 * sp;
      this.add(items, { k: 'curve', x1: x0, y1: y, cx1: xPeak, cy1: y, cx2: xPeak, cy2: topY + 1.2 * sp, x2: xPeak, y2: topY + 0.5 * sp, thick: 0.12 * sp, color: ink, id: n.id });
      this.add(items, { k: 'poly', pts: [xPeak - 0.4 * sp, topY + 0.6 * sp, xPeak + 0.4 * sp, topY + 0.6 * sp, xPeak, topY - 0.2 * sp], fill: ink, id: n.id });
      this.add(items, { k: 'text', x: xPeak, y: topY - 0.8 * sp, text: label, font: `${(1.0 * sp).toFixed(1)}px Arial, sans-serif`, color: ink, align: 'center', baseline: 'middle', id: n.id });
      if (release) {
        this.add(items, { k: 'line', x1: xPeak + 0.2 * sp, y1: topY + 0.2 * sp, x2: xPeak + 1.2 * sp, y2: y - 0.6 * sp, w: 0.12 * sp, color: ink, dash: [0.3 * sp, 0.2 * sp], id: n.id });
      }
      ext(topY - 1.4 * sp);
    }
    if (n.tap || b.tap || n.leftHandTap) {
      this.add(items, { k: 'text', x: cx, y: -1.4 * sp, text: n.leftHandTap ? '+' : 'T', font: `700 ${(1.1 * sp).toFixed(1)}px Arial, sans-serif`, color: ink, align: 'center', baseline: 'middle', id: n.id });
      ext(-2 * sp);
    }
    if (b.slap || b.pop) {
      this.add(items, { k: 'text', x: cx, y: -1.4 * sp, text: b.slap ? 'S' : 'P', font: `700 ${(1.1 * sp).toFixed(1)}px Arial, sans-serif`, color: ink, align: 'center', baseline: 'middle', id: b.id });
      ext(-2 * sp);
    }
    if (n.harmonic === 'artificial') {
      this.add(items, { k: 'text', x: cx, y: -1.4 * sp, text: 'A.H.', font: `${(1.0 * sp).toFixed(1)}px Arial, sans-serif`, color: ink, align: 'center', baseline: 'middle', id: n.id });
      ext(-2 * sp);
    }
    if (n.pullOffToNowhere) {
      this.add(items, { k: 'curve', x1: cx + 0.3 * sp, y1: y - 0.6 * sp, cx1: cx + 0.9 * sp, cy1: y - 1.4 * sp, cx2: cx + 1.5 * sp, cy2: y - 1.4 * sp, x2: cx + 2.0 * sp, y2: y - 0.4 * sp, thick: 0.14 * sp, color: ink, id: n.id });
    }
    if (n.stringCircle) {
      this.add(items, { k: 'arc', x: cx, y, r: 0.9 * sp, stroke: ink, lw: 0.1 * sp, id: n.id });
    }
    if (n.fingering || n.rhFingering) {
      const txt = [n.fingering, n.rhFingering].filter(Boolean).join(' ');
      this.add(items, { k: 'text', x: cx + textW / 2 + 0.2 * sp, y: y - 0.8 * sp, text: txt, font: `italic ${(0.95 * sp).toFixed(1)}px Georgia, serif`, color: '#555', id: n.id });
    }
  }

  // ------------------------------------------------------------ system items
  systemItems(sys: SystemBox, barBoxes: BarBox[], firstSystem: boolean, headerW: number): void {
    const { sp, ink } = this;
    const items = sys.items;
    const top = sys.top;
    const bottom = sys.bottom;
    const thin = ENGRAVING.thinBarlineThickness * sp;
    const thick = ENGRAVING.thickBarlineThickness * sp;
    const bands = sys.bands;
    // Barline spans every band (and the gaps between a track's staff and tab).
    const vline = (x: number, w: number, dash?: number[]) => this.add(items, { k: 'line', x1: x, y1: top, x2: x, y2: bottom, w, color: ink, dash });
    // System start line.
    vline(sys.x0 + this.instrumentLabelWidth(firstSystem) + (this.tracks.length > 1 ? 1.2 * sp : 0), thin);
    if (this.tracks.length > 1) {
      const bx = sys.x0 + this.instrumentLabelWidth(firstSystem) + 0.4 * sp;
      this.add(items, { k: 'rect', x: bx, y: top - 0.5 * sp, w: 0.5 * sp, h: bottom - top + sp, fill: ink });
      // Instrument labels.
      for (const t of this.tracks) {
        const tr = this.score.tracks[t];
        const label = firstSystem ? tr.name : tr.shortName;
        if (!label) continue;
        const tb = bands.filter((b) => b.track === t);
        const ym = (tb[0].top + tb[tb.length - 1].bottom) / 2;
        this.add(items, { k: 'text', x: sys.x0 + this.instrumentLabelWidth(firstSystem) - 0.8 * sp, y: ym, text: label, font: this.smallFont, color: ink, align: 'right', baseline: 'middle' });
      }
    }
    // Bar numbers at system starts (not the very first system).
    if (!firstSystem) {
      const n = this.barNumbers[barBoxes[0].bar];
      this.add(items, { k: 'text', x: sys.x0 + this.instrumentLabelWidth(false) + 0.2 * sp, y: top - 1.2 * sp, text: String(n), font: `italic ${(1.1 * sp).toFixed(1)}px Georgia, serif`, color: '#555' });
    }
    barBoxes.forEach((bbx, i) => {
      const info = this.bars[bbx.bar].info;
      // Start repeat.
      if (info.repeatStart) {
        const x = i === 0 ? sys.x0 + headerW - 0.2 * sp : bbx.x0;
        this.add(items, { k: 'line', x1: x + thick / 2, y1: top, x2: x + thick / 2, y2: bottom, w: thick, color: ink });
        this.add(items, { k: 'line', x1: x + thick + 0.4 * sp, y1: top, x2: x + thick + 0.4 * sp, y2: bottom, w: thin, color: ink });
        for (const b of bands) this.repeatDots(items, b, x + thick + 0.8 * sp);
      }
      // Right barline.
      const x = bbx.x1;
      const last = bbx.bar === this.score.bars.length - 1;
      const style = info.barline ?? (last ? 'final' : 'single');
      if (info.repeatEnd) {
        this.add(items, { k: 'line', x1: x - thick / 2, y1: top, x2: x - thick / 2, y2: bottom, w: thick, color: ink });
        this.add(items, { k: 'line', x1: x - thick - 0.4 * sp, y1: top, x2: x - thick - 0.4 * sp, y2: bottom, w: thin, color: ink });
        for (const b of bands) this.repeatDots(items, b, x - thick - 0.8 * sp - G.repeatDot.adv * sp);
      } else if (style === 'final') {
        this.add(items, { k: 'line', x1: x - thick / 2, y1: top, x2: x - thick / 2, y2: bottom, w: thick, color: ink });
        this.add(items, { k: 'line', x1: x - thick - 0.4 * sp, y1: top, x2: x - thick - 0.4 * sp, y2: bottom, w: thin, color: ink });
      } else if (style === 'double') {
        vline(x - thin / 2, thin);
        vline(x - thin / 2 - 0.45 * sp, thin);
      } else if (style === 'dashed') {
        vline(x - thin / 2, thin, [0.5 * sp, 0.3 * sp]);
      } else {
        vline(x - thin / 2, thin);
      }
    });
  }

  repeatDots(items: DrawItem[], band: Band, x: number): void {
    const { sp } = this;
    const mid = (band.top + band.bottom) / 2;
    const off = band.kind === 'staff' ? sp / 2 : band.gap / 2 + (band.lines % 2 === 0 ? 0 : 0);
    for (const y of [mid - off, mid + off]) this.add(items, { k: 'glyph', x, y, ch: G.repeatDot.ch, size: this.fontSize, color: this.ink });
  }
}

// ---------------------------------------------------------------- helpers

function sameTs(a: TimeSig, b: TimeSig): boolean {
  return a.num === b.num && a.den === b.den && (a.symbol ?? '') === (b.symbol ?? '');
}

function sameArr(a?: number[], b?: number[]): boolean {
  if (!a || !b) return false;
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function shiftItem(it: DrawItem, dx: number, dy: number): void {
  switch (it.k) {
    case 'line':
      it.x1 += dx;
      it.x2 += dx;
      it.y1 += dy;
      it.y2 += dy;
      break;
    case 'glyph':
    case 'text':
    case 'arc':
      it.x += dx;
      it.y += dy;
      break;
    case 'rect':
      it.x += dx;
      it.y += dy;
      break;
    case 'poly':
    case 'path':
      for (let i = 0; i < it.pts.length; i += 2) {
        it.pts[i] += dx;
        it.pts[i + 1] += dy;
      }
      break;
    case 'curve':
      it.x1 += dx;
      it.x2 += dx;
      it.cx1 += dx;
      it.cx2 += dx;
      it.y1 += dy;
      it.y2 += dy;
      it.cy1 += dy;
      it.cy2 += dy;
      break;
  }
}
